// OwnerSandbox — unified gallery model (owner-only AI calibration workshop).
//
// Sandbox is one chronological gallery of every sandbox photo the owner has
// ever uploaded, ordered most-recent first. The compact upload zone stays
// at the top; new uploads slot into the gallery progressively (pending
// thumbnails appear immediately with local blob URLs, then transition to
// signed URLs + detection counts as each photo finishes processing).
//
// Reflects the real workflow: drop photos in one sitting, review them
// across multiple sittings over days or weeks. Batches still exist at the
// data layer (sandbox_batches, room_scan_sessions.batch_id) carrying
// source + note, but they're metadata for future filtering — not the
// primary organizing principle.
//
// State architecture:
//   - galleryItems (component state): persisted sandbox sessions loaded
//     from DB. Source of truth for past photos.
//   - activeBatch (module-level singleton): in-flight uploads only
//     (pending + failed). Survives component remount so a navigate-away
//     mid-batch doesn't lose progress. On success, items leave activeBatch
//     and prepend to galleryItems.
//
// Owner gating: /owner is already gated in App.js (user.id === OWNER_ID).
//
// Props:
//   session   — Supabase session (provides user.id)
//   onBack    — () => void, fires when "← Owner Dashboard" tapped

import { useEffect, useRef, useState } from 'react'
import { ArrowLeft, Camera, Check, CheckSquare, Image as ImageIcon, Layers, Loader, Plus, Square, X } from 'lucide-react'
import { supabase } from '../supabase'
import { convertHeicIfNeeded } from '../utils/convertHeicIfNeeded'
import { stripExif } from '../utils/stripExif'
import { compressImage } from '../imageUtils'

const C = {
  bg: '#F2EDE4', card: '#FDFAF6', border: '#DDD4C6',
  amber: '#B8721F', amberLight: '#D4902A', brown: '#2C1D14',
  muted: '#9A7B68', success: '#2E6B4A', successBg: '#EBF5EF',
  danger: '#B03020', dangerBg: '#FDECEA',
}
const f = 'Georgia, serif'
const fs = 'Calibri, sans-serif'

const SPIN = `
@keyframes ownersandbox-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
.os-sandbox-select:hover, .os-sandbox-select:focus {
  border-color: #B8721F !important;
  outline: none;
}
.os-sandbox-chip-x:hover, .os-sandbox-chip-x:focus {
  color: #B03020 !important;
  background: rgba(176,48,32,0.1) !important;
  outline: none;
}
.os-sandbox-readlabel:hover:not(:disabled), .os-sandbox-readlabel:focus:not(:disabled) {
  border-color: #B8721F !important;
  outline: none;
}
.os-sandbox-recent-row[style*="cursor: pointer"]:hover {
  background: #F2EDE4;
}
`

const SELECT_CHEVRON_BG = `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23B8721F' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'><polyline points='6 9 12 15 18 9'/></svg>")`

const SOURCE_OPTIONS = [
  'Open house',
  'Hardware store',
  "Friend's home",
  'From advisor',
  'Other',
]

// Categories the user can assign to a grouped sandbox_object. Mirrors
// VALID_CATEGORIES in the enumerate-room-items EF prompts. Keep in sync
// when the EF list changes.
const SANDBOX_OBJECT_CATEGORIES = [
  'Furniture', 'Electronics', 'Appliances',
  'Housewares', 'Linens', 'Décor',
  'Bags & Luggage', 'Books & Media',
  'Jewelry', 'Art', 'Clothing', 'Tools',
  'Musical Instruments', 'Sports Equipment', 'Collectibles',
  'Other',
]

const MAX_BATCH_SIZE = 100
const BATCH_CONCURRENCY = 5
const GALLERY_PAGE_SIZE = 30
const SOURCE_PREF_STORAGE_KEY = 'casavio.ownersandbox.lastSource'

// Drill-in photo zoom limits — shared by tap-toggle, wheel, and pinch.
const ZOOM_MIN = 1.0
const ZOOM_MAX = 4.0
const ZOOM_WHEEL_STEP = 0.1
const ZOOM_PINCH_SNAP_THRESHOLD = 1.05

// Labels mirror the user-facing phrasing reviewed during testing.
// inputKind controls what the form shows below the dropdown:
//   'text'      — free-text "Correct value" input
//   'none'      — no input; the type itself is the whole correction
//                  (false_positive — detection shouldn't exist)
//   'duplicate' — dropdown of OTHER detections in the same photo; the
//                  chosen detection_id is stored as user_value
const CORRECTION_TYPES = [
  { value: 'label', label: 'Label is wrong', inputKind: 'text', fieldName: 'refined_label' },
  { value: 'brand', label: 'Brand is wrong or missing', inputKind: 'text', fieldName: 'brand.value' },
  { value: 'model', label: 'Model is wrong or missing', inputKind: 'text', fieldName: 'model.value' },
  { value: 'serial', label: 'Serial number is wrong or missing', inputKind: 'text', fieldName: 'serial' },
  { value: 'manufactured', label: 'Manufactured date is wrong', inputKind: 'text', fieldName: 'manufactured' },
  { value: 'category', label: 'Category is wrong', inputKind: 'text', fieldName: 'category_suggestion' },
  { value: 'false_positive', label: "False positive — this isn't really here", inputKind: 'none', fieldName: null },
  { value: 'not_relevant', label: 'Not worth tracking (box of crackers, mail, consumables, etc.)', inputKind: 'none', fieldName: null },
  { value: 'duplicate', label: 'Duplicate of another detection', inputKind: 'duplicate', fieldName: null },
]

function aiValueForType(item, type) {
  const d = item?.ai_details
  switch (type) {
    case 'label': return item?.refined_label || item?.label || ''
    case 'brand': return d?.brand?.value || ''
    case 'model': return d?.model?.value || ''
    case 'serial': return d?.serial || ''
    case 'manufactured': return d?.manufactured || ''
    case 'category': return d?.category_suggestion || ''
    default: return ''
  }
}

// Display labels for the analytics "Corrections by type" bar chart.
// Distinct from chipLabelFor (which formats per-row with values inline) —
// this is the type taxonomy as the user thinks of it in aggregate.
const CORRECTION_TYPE_DISPLAY = {
  label: 'Label is wrong',
  brand: 'Brand is wrong',
  model: 'Model is wrong',
  serial: 'Serial is wrong',
  manufactured: 'Manufactured date is wrong',
  category: 'Category is wrong',
  missed: 'Casavio missed something',
  false_positive: 'False positive',
  duplicate: 'Duplicate',
  not_relevant: 'Not worth tracking',
  other: 'Other',
}

// Bucket a timestamp into the Sunday-start ISO date of its week.
function weekStartIso(iso) {
  const d = new Date(iso)
  const day = d.getDay()
  d.setDate(d.getDate() - day)
  d.setHours(0, 0, 0, 0)
  return d.toISOString().slice(0, 10)
}

function shortWeekLabel(weekIso) {
  if (!weekIso) return ''
  const d = new Date(weekIso + 'T00:00:00')
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

// Expanded chip-label phrasing for the section-level chips (photo-level
// and group-level views). Uses the analytics display strings + the user
// value, so a chip in the dedicated section reads like a sentence
// ("Label is wrong: 'Maytag'") instead of the inline shorthand
// ("Label: 'Maytag'") used on a detection row.
function chipLabelExpanded(c) {
  const typeLabel = CORRECTION_TYPE_DISPLAY[c?.correction_type] || c?.correction_type || 'Correction'
  const noneTypes = new Set(['false_positive', 'not_relevant'])
  if (noneTypes.has(c?.correction_type)) return typeLabel
  if (c?.correction_type === 'duplicate') return typeLabel
  const value = c?.user_value
  if (!value || !value.trim()) return typeLabel
  return `${typeLabel}: "${value}"`
}

// Short chip-label phrasing for an existing correction. The chip appears
// under each detection in the drill-in; the user value goes in quotes for
// text-based types, while inputKind='none' types collapse to a verb.
function chipLabelFor(c) {
  const v = c?.user_value
  switch (c?.correction_type) {
    case 'label':        return v ? `Label: "${v}"` : 'Label'
    case 'brand':        return v ? `Brand: "${v}"` : 'Brand'
    case 'model':        return v ? `Model: "${v}"` : 'Model'
    case 'serial':       return v ? `Serial: "${v}"` : 'Serial'
    case 'manufactured': return v ? `Manufactured: "${v}"` : 'Manufactured'
    case 'category':     return v ? `Category: "${v}"` : 'Category'
    case 'false_positive': return 'False positive'
    case 'not_relevant':   return 'Not worth tracking'
    case 'duplicate':      return 'Duplicate of another'
    case 'missed':         return v ? `Missed: "${v}"` : 'Missed'
    case 'other':          return 'Other'
    default:               return c?.correction_type || 'Correction'
  }
}

function relTime(iso) {
  if (!iso) return ''
  const now = Date.now()
  const t = new Date(iso).getTime()
  const s = Math.max(0, Math.floor((now - t) / 1000))
  if (s < 60) return `${s}s ago`
  if (s < 3600) return `${Math.floor(s / 60)}m ago`
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`
  return `${Math.floor(s / 86400)}d ago`
}

function shortDate(iso) {
  if (!iso) return ''
  const d = new Date(iso)
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

async function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader()
    r.onload = (e) => resolve(String(e.target.result).split(',')[1] || '')
    r.onerror = () => reject(new Error('Failed to read file'))
    r.readAsDataURL(file)
  })
}

// Parse Supabase storage bucket+path out of a signed or public URL. Used
// by the delete-photo flow which has no other handle on the storage path
// (room_scan_sessions doesn't persist it; only the signed URL is stored).
// Returns { bucket, path } or null if the URL doesn't match the expected
// pattern (e.g. third-party CDN, malformed input).
function parseStoragePathFromUrl(url) {
  if (!url) return null
  try {
    const u = new URL(url)
    const m = u.pathname.match(/\/storage\/v1\/(?:object|render\/image)\/(?:sign|public)\/([^/]+)\/(.+)$/)
    if (!m) return null
    return { bucket: m[1], path: decodeURIComponent(m[2]) }
  } catch (_) {
    return null
  }
}

// ---------------------------------------------------------------------------
// Module-level inflight batch state — narrowed scope vs. previous version.
// activeBatch now only carries PENDING and FAILED uploads. Successful uploads
// are immediately moved into the component's galleryItems with their session
// data, so they're rendered from there. This avoids a render dedup pass and
// matches the gallery model (gallery = persisted truth).
//
// Survives component remount so a navigate-away mid-batch doesn't lose
// progress. Listeners are setState callbacks that bump a tick counter.
// ---------------------------------------------------------------------------

let activeBatch = null
/* activeBatch shape:
   {
     batchId,            // sandbox_batches.id (uuid)
     source,             // batch-level source string
     note,               // batch-level note (string | null)
     startedAt,          // ms
     totalCount,         // for the in-flight pill denominator
     pending: [
       { fileId, fileName, file, blobUrl }
     ],
     failed: [
       { fileId, fileName, file, blobUrl, error }
     ],
     // Component callback for prepend-to-gallery on success. Set by the
     // component on mount; cleared on unmount. If null (component unmounted
     // mid-batch), successful items just complete silently — the gallery
     // query on next mount picks them up from DB.
     onItemSuccess: null,
   }
*/

const batchListeners = new Set()
function notifyBatchListeners() {
  for (const l of batchListeners) {
    try { l() } catch (_) { /* noop */ }
  }
}

function isBatchInFlight() {
  return !!activeBatch && activeBatch.pending.length > 0
}

async function processOneInBatch(fileId, homeId) {
  if (!activeBatch) return
  const pendingIdx = activeBatch.pending.findIndex(p => p.fileId === fileId)
  if (pendingIdx === -1) return
  const item = activeBatch.pending[pendingIdx]
  try {
    const jpeg = await convertHeicIfNeeded(item.file)
    const stripped = await stripExif(jpeg)
    const processed = await compressImage(stripped, 1600, 0.80)
    const base64 = await fileToBase64(processed)
    const { data, error } = await supabase.functions.invoke('enumerate-room-items', {
      body: {
        capture_mode: 'sandbox',
        photo_base64: base64,
        home_id: homeId,
        room_id: null,
        media_type: 'image/jpeg',
        sandbox_source: activeBatch?.source,
        sandbox_note: activeBatch?.note,
        batch_id: activeBatch?.batchId,
      },
    })
    if (error) throw new Error(error.message || 'Edge Function error')
    if (!data?.session_id) throw new Error('Unexpected response shape')
    if (!activeBatch) return // component cleared the batch mid-flight
    // Build a gallery entry from the response.
    const galleryEntry = {
      sessionId: data.session_id,
      createdAt: new Date().toISOString(),
      photoUrl: data.photo_url || item.blobUrl,
      itemsDetected: Array.isArray(data.items) ? data.items.length : 0,
      correctionsCount: 0,
      status: 'reviewing',
      batchSource: activeBatch.source,
      batchNote: activeBatch.note,
      batchId: activeBatch.batchId,
      sandboxObjectId: null,
      objectCanonicalLabel: null,
      objectPrimarySessionId: null,
      reviewedAt: null,
      // Cache the items so drill-in doesn't need to re-fetch for just-completed
      items: Array.isArray(data.items) ? data.items : [],
    }
    // Remove from pending
    activeBatch.pending = activeBatch.pending.filter(p => p.fileId !== fileId)
    // Revoke the blob URL — gallery now uses the signed URL
    if (item.blobUrl) {
      try { URL.revokeObjectURL(item.blobUrl) } catch (_) { /* noop */ }
    }
    // Hand off to the component if subscribed
    if (activeBatch.onItemSuccess) {
      activeBatch.onItemSuccess(galleryEntry)
    }
    notifyBatchListeners()
  } catch (err) {
    console.error('[OwnerSandbox] batch photo error', fileId, err)
    if (!activeBatch) return
    // Move from pending to failed
    activeBatch.pending = activeBatch.pending.filter(p => p.fileId !== fileId)
    activeBatch.failed = [
      ...activeBatch.failed,
      { ...item, error: err.message || String(err) },
    ]
    notifyBatchListeners()
  }
}

async function runBatchWorkers(homeId) {
  if (!activeBatch) return
  // Snapshot the queue at start. New entries from retries go through a
  // separate dispatch (processOneInBatch directly) — they don't run via
  // the worker pool.
  const queue = activeBatch.pending.map(p => p.fileId)
  let nextIdx = 0
  function take() {
    if (!activeBatch) return null
    if (nextIdx >= queue.length) return null
    return queue[nextIdx++]
  }
  const workers = Array.from({ length: BATCH_CONCURRENCY }, async () => {
    while (true) {
      const fileId = take()
      if (fileId === null) break
      await processOneInBatch(fileId, homeId)
    }
  })
  await Promise.all(workers)
}

function clearActiveBatchIfIdle() {
  if (!activeBatch) return
  if (activeBatch.pending.length > 0) return
  if (activeBatch.failed.length > 0) return
  activeBatch = null
  notifyBatchListeners()
}

// Fire-and-forget increment of sandbox_batches.corrections_count.
async function bumpBatchCorrectionsCount(batchId) {
  if (!batchId) return
  try {
    const { data } = await supabase
      .from('sandbox_batches')
      .select('corrections_count')
      .eq('id', batchId)
      .maybeSingle()
    const current = data?.corrections_count ?? 0
    const { data: updated, error: updErr } = await supabase
      .from('sandbox_batches')
      .update({ corrections_count: current + 1 })
      .eq('id', batchId)
      .select('id')
    if (updErr) console.error('[OwnerSandbox] bumpBatchCorrectionsCount UPDATE error', updErr)
    else if (!updated || updated.length === 0) {
      console.error('[OwnerSandbox] bumpBatchCorrectionsCount affected 0 rows — RLS or batch missing', batchId)
    }
  } catch (err) {
    console.error('[OwnerSandbox] bumpBatchCorrectionsCount failed', err)
  }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function OwnerSandbox({ session, onBack }) {
  // Force re-render on any activeBatch mutation.
  const [, setBatchTick] = useState(0)
  useEffect(() => {
    const listener = () => setBatchTick(t => t + 1)
    batchListeners.add(listener)
    return () => { batchListeners.delete(listener) }
  }, [])

  // Upload form state
  const [selectedFiles, setSelectedFiles] = useState([]) // [{ fileId, file, blobUrl }]
  const [source, setSource] = useState(() => {
    try {
      const saved = localStorage.getItem(SOURCE_PREF_STORAGE_KEY)
      if (saved && SOURCE_OPTIONS.includes(saved)) return saved
    } catch (_) { /* noop */ }
    return SOURCE_OPTIONS[0]
  })
  const [note, setNote] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState(null)

  // Multi-input UX
  const [isDraggingOver, setIsDraggingOver] = useState(false)
  const [pasteToast, setPasteToast] = useState(null)

  // Gallery state
  const [galleryItems, setGalleryItems] = useState([])
  const [galleryOffset, setGalleryOffset] = useState(0)
  const [galleryHasMore, setGalleryHasMore] = useState(true)
  const [galleryLoading, setGalleryLoading] = useState(false)
  const [galleryInitialLoading, setGalleryInitialLoading] = useState(true)

  // Drill-in modal state
  const [drillInSessionId, setDrillInSessionId] = useState(null)
  const [drillInLoading, setDrillInLoading] = useState(false)
  const [drillInItems, setDrillInItems] = useState([])
  const [filteredOutExpanded, setFilteredOutExpanded] = useState(false)

  // Per-session corrections (active only — soft-deleted hidden). Drives
  // the chip list rendered under each detection in the drill-in.
  const [sessionCorrections, setSessionCorrections] = useState([])

  // When set, the correction form is editing this row (insert new +
  // soft-delete this one with replaced_by_id linkage). Null = create new.
  const [editingCorrectionId, setEditingCorrectionId] = useState(null)

  // Soft-delete confirmation modal — { correctionId, label } or null.
  const [confirmDeleteCorrection, setConfirmDeleteCorrection] = useState(null)

  // Detection id currently mid-flight in the deep-read EF, or null. Drives
  // the per-row spinner so the user gets feedback during the ~10-15s call.
  const [deepReadingDetectionId, setDeepReadingDetectionId] = useState(null)

  // Top-level sandbox tab: 'gallery' (default — upload + grid + drill-in)
  // or 'analytics' (read-only dashboard). Switching tabs preserves gallery
  // state; analytics refetches on each open so counts reflect latest edits.
  const [sandboxTab, setSandboxTab] = useState('gallery')
  const [analyticsData, setAnalyticsData] = useState(null)
  const [analyticsLoading, setAnalyticsLoading] = useState(false)

  // Multi-select mode for grouping photos into one sandbox_object. Once
  // active, gallery thumbnail clicks toggle selection instead of opening
  // the drill-in. selectedSessionIds is a Set for O(1) has/add/delete.
  const [selectMode, setSelectMode] = useState(false)
  const [selectedSessionIds, setSelectedSessionIds] = useState(() => new Set())

  // Group-creation modal + form state.
  const [groupModalOpen, setGroupModalOpen] = useState(false)
  const [groupForm, setGroupForm] = useState({
    label: '', make: '', model: '', serial: '', category: '', notes: '',
  })
  const [creatingGroup, setCreatingGroup] = useState(false)
  // Per-field AI pre-fill metadata. Each entry is { mode, options } where
  // mode is 'single' (one agreed value across selected sessions),
  // 'multi' (distinct values — render as dropdown), or 'empty' (no AI
  // data). Options are [{ value, count }] for multi mode.
  const [prefillModes, setPrefillModes] = useState({})
  // Field names where the user chose "Type a new X…" — swaps the multi
  // dropdown back to a free-text input for that field.
  const [customFieldModes, setCustomFieldModes] = useState(() => new Set())
  const [prefillLoading, setPrefillLoading] = useState(false)

  // Grouped-object detail sub-view. When viewingGroupId is set, the
  // gallery/analytics body is replaced by renderGroupedObject().
  const [viewingGroupId, setViewingGroupId] = useState(null)
  const [viewingGroupData, setViewingGroupData] = useState(null) // { object, sessions, corrections, detectionsByDetectionId, correctionsLoading }
  // When an edit is triggered from a group-level chip, we open the drill-in
  // for the chip's session and stash the correction here. A useEffect picks
  // it up once drill-in items load and opens the edit form.
  const [pendingGroupChipEdit, setPendingGroupChipEdit] = useState(null)
  // Tracks whether the in-progress edit was triggered from a group chip,
  // so we can auto-close the drill-in on save and return the user to the
  // grouped detail view.
  const [groupChipEditActive, setGroupChipEditActive] = useState(false)
  const [viewingGroupLoading, setViewingGroupLoading] = useState(false)
  const [confirmUngroup, setConfirmUngroup] = useState(false)
  const [ungrouping, setUngrouping] = useState(false)

  // Reviewed-photos section. Collapsed by default; expanding fires a
  // dedicated query (cap 100). Optimistic update marks/unmarks happen
  // on the active galleryItems + reviewedItems lists simultaneously.
  const [reviewedExpanded, setReviewedExpanded] = useState(false)
  const [reviewedItems, setReviewedItems] = useState([])
  const [reviewedLoading, setReviewedLoading] = useState(false)
  const [reviewedHitCap, setReviewedHitCap] = useState(false)
  const [markingReviewed, setMarkingReviewed] = useState(false)

  // Drag-to-reorder state for the grouped object detail photo grid.
  // Per spec: primary_session_id is the only persisted reorder; other
  // drops are visual-only and ephemeral (no DB write).
  const [dragSessionId, setDragSessionId] = useState(null)
  const [dragOverIndex, setDragOverIndex] = useState(null)

  // Drill-in photo zoom + pan state. scale toggles 1 ↔ 2; x/y in CSS pixels
  // applied as transform translate. Reset on drill-in open/close and when
  // photoUrl changes (different photo).
  const [photoZoom, setPhotoZoom] = useState({ scale: 1, x: 0, y: 0 })

  // Delete-photo confirmation state
  const [confirmDeleteSession, setConfirmDeleteSession] = useState(null)
  const [deleting, setDeleting] = useState(false)

  // Correction form state (only meaningful when drill-in is open)
  const [activeCorrection, setActiveCorrection] = useState(null)
  const [correctionType, setCorrectionType] = useState('label')
  const [correctionValue, setCorrectionValue] = useState('')
  const [correctionNotes, setCorrectionNotes] = useState('')
  const [savingCorrection, setSavingCorrection] = useState(false)
  const [missedMode, setMissedMode] = useState(false)

  // User context
  const [homeId, setHomeId] = useState(null)
  const [recentCorrections, setRecentCorrections] = useState([])

  const cameraInputRef = useRef(null)
  const libraryInputRef = useRef(null)
  const imgRef = useRef(null)
  const selectedBlobsRef = useRef([])
  const sentinelRef = useRef(null)
  const photoViewportRef = useRef(null)
  // Live drag tracking for photo pan. Lives in a ref to avoid stale-closure
  // issues inside pointer handlers and to prevent re-renders on every move.
  const panDragRef = useRef({ down: false, startX: 0, startY: 0, originX: 0, originY: 0, moved: false })
  // Live pinch tracking. Records the two-finger distance, midpoint, and
  // transform snapshot taken at pinch start so each touchmove computes a
  // direct ratio rather than accumulating frame-to-frame.
  const pinchRef = useRef({
    active: false,
    initialDistance: 0,
    initialScale: 1,
    initialAnchorX: 0,
    initialAnchorY: 0,
    initialOriginX: 0,
    initialOriginY: 0,
  })

  // Persist source preference across uploads.
  useEffect(() => {
    try { localStorage.setItem(SOURCE_PREF_STORAGE_KEY, source) } catch (_) { /* noop */ }
  }, [source])

  // Subscribe activeBatch's onItemSuccess to the component's prepend handler.
  // Idempotent prepend via sessionId dedup handles the race where a remount
  // happens between batch completion and the gallery query refetch.
  useEffect(() => {
    if (!activeBatch) return
    activeBatch.onItemSuccess = (entry) => {
      setGalleryItems(prev => {
        if (prev.some(g => g.sessionId === entry.sessionId)) return prev
        return [entry, ...prev]
      })
    }
    return () => {
      if (activeBatch) activeBatch.onItemSuccess = null
    }
  }, [/* re-subscribe whenever activeBatch identity changes — module-level
       singleton makes this awkward; the listener bump triggers re-renders
       that will hit this effect's setup. */])

  // Load home_id + recent corrections + first gallery page on mount.
  useEffect(() => {
    let cancelled = false
    async function load() {
      const userId = session?.user?.id
      if (!userId) return
      const { data: hm } = await supabase
        .from('home_members')
        .select('home_id')
        .eq('user_id', userId)
        .limit(1)
        .maybeSingle()
      if (!cancelled && hm?.home_id) setHomeId(hm.home_id)
      loadRecentCorrections()
      loadGalleryPage(0, true)
    }
    load()
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.user?.id])

  // Revoke any pre-submit blob URLs on unmount (post-submit blobs are
  // owned by activeBatch and persist across remount).
  useEffect(() => {
    return () => {
      for (const url of selectedBlobsRef.current) {
        try { URL.revokeObjectURL(url) } catch (_) { /* noop */ }
      }
      selectedBlobsRef.current = []
    }
  }, [])

  async function loadRecentCorrections() {
    const userId = session?.user?.id
    if (!userId) return
    // Embed the parent session so each row carries its thumbnail URL +
    // group membership — used by the Recent Corrections row click target
    // to route to either openDrillIn (ungrouped) or openGroupDetail
    // (grouped). One FK between ai_corrections and room_scan_sessions,
    // so no PostgREST disambiguation needed.
    const { data } = await supabase
      .from('ai_corrections')
      .select(`
        id, correction_type, ai_value, user_value, context_source, notes,
        created_at, session_id,
        room_scan_sessions ( source_photo_urls, sandbox_object_id )
      `)
      .eq('user_id', userId)
      .is('deleted_at', null)
      .order('created_at', { ascending: false })
      .limit(10)
    setRecentCorrections(data || [])
  }

  // Pull every analytics query in parallel and reduce to the shape the
  // Analytics tab renders. Cheap for current data sizes (single-user
  // owner-only); revisit if this ever grows past ~10k corrections.
  async function loadAnalytics() {
    const userId = session?.user?.id
    if (!userId) return
    setAnalyticsLoading(true)
    try {
      // 1. Sandbox session ids (used both as headline count + filter for #2)
      const sessionsP = supabase
        .from('room_scan_sessions')
        .select('id')
        .eq('capture_mode', 'sandbox')
        .eq('user_id', userId)
      // 3. Active corrections count
      const activeCorrectionsP = supabase
        .from('ai_corrections')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', userId)
        .is('deleted_at', null)
      // 4 + 5. Per-correction rows for by-type + by-week grouping
      const byTypeRowsP = supabase
        .from('ai_corrections')
        .select('correction_type, created_at')
        .eq('user_id', userId)
        .is('deleted_at', null)
        .order('created_at', { ascending: true })
      // 6. Sources — inner-join to sandbox sessions only, exclude missed
      //    corrections (no detection_id) per the spec's join shape.
      const sourcesRowsP = supabase
        .from('ai_corrections')
        .select('id, session_id, room_scan_sessions!inner(sandbox_source, capture_mode)')
        .eq('user_id', userId)
        .is('deleted_at', null)
        .not('detection_id', 'is', null)
        .eq('room_scan_sessions.capture_mode', 'sandbox')
      // 7. Deep-read count
      const deepReadsP = supabase
        .from('room_photo_detections')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', userId)
        .not('last_deep_read_at', 'is', null)

      const [sessionsRes, activeCorrRes, byTypeRes, sourcesRes, deepReadsRes] =
        await Promise.all([sessionsP, activeCorrectionsP, byTypeRowsP, sourcesRowsP, deepReadsP])

      const sessionIds = (sessionsRes.data || []).map(s => s.id)
      // 2. Detections count — filter by sandbox session ids so we don't
      //    over-count detections from production photo/room scans.
      let detectionsCount = 0
      if (sessionIds.length > 0) {
        const { count } = await supabase
          .from('room_photo_detections')
          .select('id', { count: 'exact', head: true })
          .in('session_id', sessionIds)
        detectionsCount = count ?? 0
      }

      // Aggregate corrections by type
      const typeCounts = new Map()
      for (const row of byTypeRes.data || []) {
        typeCounts.set(row.correction_type, (typeCounts.get(row.correction_type) || 0) + 1)
      }
      const byType = Array.from(typeCounts.entries())
        .map(([type, count]) => ({ type, count }))
        .sort((a, b) => b.count - a.count)

      // Aggregate corrections by week (Sunday-start)
      const weekCounts = new Map()
      for (const row of byTypeRes.data || []) {
        const wk = weekStartIso(row.created_at)
        weekCounts.set(wk, (weekCounts.get(wk) || 0) + 1)
      }
      const byWeek = Array.from(weekCounts.entries())
        .map(([week, count]) => ({ week, count }))
        .sort((a, b) => a.week.localeCompare(b.week))

      // Aggregate sources
      const sourceMap = new Map()
      for (const row of sourcesRes.data || []) {
        const src = row.room_scan_sessions?.sandbox_source || '(uncategorized)'
        const entry = sourceMap.get(src) || { source: src, correction_count: 0, sessions: new Set() }
        entry.correction_count++
        if (row.session_id) entry.sessions.add(row.session_id)
        sourceMap.set(src, entry)
      }
      const bySource = Array.from(sourceMap.values())
        .map(e => ({ source: e.source, correction_count: e.correction_count, photo_count: e.sessions.size }))
        .sort((a, b) => b.correction_count - a.correction_count)
        .slice(0, 10)

      setAnalyticsData({
        photosAnalyzed: sessionIds.length,
        detectionsCount,
        activeCorrections: activeCorrRes.count ?? 0,
        byType,
        byWeek,
        bySource,
        deepReads: deepReadsRes.count ?? 0,
      })
    } catch (err) {
      console.error('[OwnerSandbox] loadAnalytics error', err)
      setAnalyticsData(null)
    } finally {
      setAnalyticsLoading(false)
    }
  }

  // Refetch on every transition into the Analytics tab so counts reflect
  // edits made in the gallery without the user needing to refresh.
  useEffect(() => {
    if (sandboxTab !== 'analytics') return
    loadAnalytics()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sandboxTab])

  // Fetch all active corrections for a single drill-in session. Used to
  // render the per-detection chip list and to drive edit prefill.
  async function loadSessionCorrections(sessionId) {
    if (!sessionId) {
      setSessionCorrections([])
      return
    }
    const { data, error } = await supabase
      .from('ai_corrections')
      .select('id, correction_type, field_name, ai_value, user_value, notes, detection_id, created_at, bounding_box')
      .eq('session_id', sessionId)
      .is('deleted_at', null)
      .order('created_at', { ascending: true })
    if (error) {
      console.error('[OwnerSandbox] loadSessionCorrections error', error)
      setSessionCorrections([])
      return
    }
    setSessionCorrections(data || [])
  }

  async function loadGalleryPage(offset, isInitial) {
    const userId = session?.user?.id
    if (!userId) return
    if (galleryLoading) return
    setGalleryLoading(true)
    if (isInitial) setGalleryInitialLoading(true)
    try {
      const { data: sessions, error } = await supabase
        .from('room_scan_sessions')
        .select(`
          id, created_at, source_photo_urls, items_detected, status,
          sandbox_source, sandbox_note, batch_id, sandbox_object_id, reviewed_at,
          sandbox_batches ( source, note ),
          sandbox_objects!sandbox_object_id ( canonical_label, primary_session_id )
        `)
        .eq('capture_mode', 'sandbox')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .range(offset, offset + GALLERY_PAGE_SIZE - 1)
      if (error) throw error
      const sessionRows = sessions || []
      // Second pass: pull correction counts for these sessions.
      // Single-user sandbox = small volume; a flat query per page is fine.
      let countMap = new Map()
      if (sessionRows.length > 0) {
        const ids = sessionRows.map(s => s.id)
        const { data: corrs } = await supabase
          .from('ai_corrections')
          .select('session_id')
          .in('session_id', ids)
          .is('deleted_at', null)
        for (const c of corrs || []) {
          countMap.set(c.session_id, (countMap.get(c.session_id) || 0) + 1)
        }
      }
      const newItems = sessionRows.map(s => ({
        sessionId: s.id,
        createdAt: s.created_at,
        photoUrl: s.source_photo_urls?.[0] || null,
        itemsDetected: s.items_detected ?? 0,
        correctionsCount: countMap.get(s.id) || 0,
        status: s.status,
        batchSource: s.sandbox_batches?.source || null,
        batchNote: s.sandbox_batches?.note || null,
        sandboxSource: s.sandbox_source || null,
        sandboxNote: s.sandbox_note || null,
        batchId: s.batch_id || null,
        sandboxObjectId: s.sandbox_object_id || null,
        // Joined from sandbox_objects via the sandbox_object_id FK. Null
        // when the session isn't grouped.
        objectCanonicalLabel: s.sandbox_objects?.canonical_label || null,
        objectPrimarySessionId: s.sandbox_objects?.primary_session_id || null,
        reviewedAt: s.reviewed_at || null,
        items: null, // lazy-loaded on drill-in
      }))
      setGalleryItems(prev => {
        // Dedup by sessionId — activeBatch's prepend may have added an item
        // that this query also returns. Keep the existing entry (it likely
        // has cached items from the just-completed response).
        const existing = new Set(prev.map(p => p.sessionId))
        const merged = isInitial ? [] : [...prev]
        for (const ni of newItems) {
          if (!existing.has(ni.sessionId)) merged.push(ni)
        }
        if (isInitial) {
          // On initial load, prepend any activeBatch successes that already
          // landed in galleryItems. (None on first mount; relevant on remount
          // where the prepend handler fired but the component was unmounted.)
          return merged
        }
        return merged
      })
      setGalleryOffset(offset + newItems.length)
      setGalleryHasMore(sessionRows.length === GALLERY_PAGE_SIZE)
    } catch (err) {
      console.error('[OwnerSandbox] gallery load error', err)
    } finally {
      setGalleryLoading(false)
      if (isInitial) setGalleryInitialLoading(false)
    }
  }

  // ESC key — close in priority order so the most-foreground UI wins:
  // chip delete confirm → session delete confirm → correction form →
  // drill-in itself. No-op when no drill-in is open.
  useEffect(() => {
    if (drillInSessionId === null) return
    function onKey(e) {
      if (e.key !== 'Escape') return
      if (confirmDeleteCorrection) {
        setConfirmDeleteCorrection(null)
      } else if (confirmDeleteSession) {
        if (!deleting) setConfirmDeleteSession(null)
      } else if (activeCorrection) {
        setActiveCorrection(null)
        setEditingCorrectionId(null)
      } else {
        closeDrillIn()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drillInSessionId, confirmDeleteCorrection, confirmDeleteSession, activeCorrection, deleting])

  // Infinite scroll via IntersectionObserver on the sentinel.
  useEffect(() => {
    if (!sentinelRef.current) return
    if (!galleryHasMore) return
    if (drillInSessionId !== null) return // pause while modal open
    const obs = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && !galleryLoading && galleryHasMore) {
        loadGalleryPage(galleryOffset, false)
      }
    }, { rootMargin: '400px 0px' })
    obs.observe(sentinelRef.current)
    return () => obs.disconnect()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [galleryOffset, galleryHasMore, galleryLoading, drillInSessionId])

  // ----- File selection (picker, drag/drop, paste all converge) --------

  function addImageFiles(newFiles) {
    const filtered = newFiles.filter(f => f && (f.type || '').startsWith('image/'))
    if (filtered.length === 0) return { added: 0, truncated: false, atCap: false }
    const remaining = MAX_BATCH_SIZE - selectedFiles.length
    if (remaining <= 0) return { added: 0, truncated: false, atCap: true }
    const toAdd = filtered.slice(0, remaining)
    const truncated = filtered.length > remaining
    const entries = toAdd.map(file => {
      const blobUrl = URL.createObjectURL(file)
      selectedBlobsRef.current.push(blobUrl)
      return {
        fileId: `f-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        file,
        blobUrl,
      }
    })
    setSelectedFiles(prev => [...prev, ...entries])
    return { added: toAdd.length, truncated, atCap: false }
  }

  function handleFilesPicked(e) {
    const newFiles = Array.from(e.target.files || [])
    e.target.value = ''
    const result = addImageFiles(newFiles)
    if (result.atCap) {
      alert(`Already at the ${MAX_BATCH_SIZE}-photo limit per batch. Remove some before adding more.`)
    } else if (result.truncated) {
      alert(`Maximum ${MAX_BATCH_SIZE} photos per batch. Pro plan unlocks larger batches.\n\nAdded ${result.added} of ${newFiles.length} photos.`)
    }
  }

  function removeSelectedFile(fileId) {
    setSelectedFiles(prev => {
      const removed = prev.find(s => s.fileId === fileId)
      if (removed?.blobUrl) {
        try { URL.revokeObjectURL(removed.blobUrl) } catch (_) { /* noop */ }
        selectedBlobsRef.current = selectedBlobsRef.current.filter(u => u !== removed.blobUrl)
      }
      return prev.filter(s => s.fileId !== fileId)
    })
  }

  // ----- Drag & drop ---------------------------------------------------

  function handleDragOver(e) {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'copy'
    if (!isDraggingOver) setIsDraggingOver(true)
  }
  function handleDragLeave(e) {
    // relatedTarget-aware: don't clear when crossing child boundaries.
    if (e.currentTarget.contains(e.relatedTarget)) return
    setIsDraggingOver(false)
  }
  function handleDrop(e) {
    e.preventDefault()
    setIsDraggingOver(false)
    const dropped = Array.from(e.dataTransfer?.files || [])
    if (dropped.length === 0) return
    const result = addImageFiles(dropped)
    if (result.atCap) {
      alert(`Already at the ${MAX_BATCH_SIZE}-photo limit per batch. Remove some before adding more.`)
    } else if (result.truncated) {
      alert(`Maximum ${MAX_BATCH_SIZE} photos per batch. Pro plan unlocks larger batches.\n\nAdded ${result.added} of ${dropped.length} photos.`)
    }
  }

  // ----- Clipboard paste ----------------------------------------------

  useEffect(() => {
    // Disable paste when drill-in modal is open (modal should own its own
    // input semantics; clipboard text in inputs continues to work either way).
    if (drillInSessionId !== null) return
    function handlePaste(e) {
      const items = Array.from(e.clipboardData?.items || [])
      const images = []
      for (const item of items) {
        if (item.kind === 'file' && (item.type || '').startsWith('image/')) {
          const file = item.getAsFile()
          if (!file) continue
          const ext = (file.type.split('/')[1] || 'png').replace(/[^a-z0-9]/gi, '')
          const ts = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19)
          const named = new File([file], `pasted-${ts}-${images.length + 1}.${ext}`, { type: file.type })
          images.push(named)
        }
      }
      if (images.length === 0) return
      e.preventDefault()
      const result = addImageFiles(images)
      if (result.atCap) {
        setPasteToast(`Already at ${MAX_BATCH_SIZE} photos — paste rejected.`)
      } else if (result.added > 0) {
        const note = result.truncated ? ` (${images.length - result.added} skipped — at cap)` : ''
        setPasteToast(`Pasted ${result.added} photo${result.added === 1 ? '' : 's'} from clipboard${note}`)
      }
    }
    window.addEventListener('paste', handlePaste)
    return () => window.removeEventListener('paste', handlePaste)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drillInSessionId, selectedFiles.length])

  useEffect(() => {
    if (!pasteToast) return
    const t = setTimeout(() => setPasteToast(null), 2500)
    return () => clearTimeout(t)
  }, [pasteToast])

  // ----- Batch submit --------------------------------------------------

  async function handleSubmit() {
    if (selectedFiles.length === 0 || !homeId || submitting) return
    if (isBatchInFlight()) {
      setSubmitError('Another batch is still processing. Wait for it to finish (or check the gallery for the in-flight pill).')
      return
    }
    setSubmitting(true)
    setSubmitError(null)
    try {
      const { data: batchRow, error: batchErr } = await supabase
        .from('sandbox_batches')
        .insert({
          user_id: session.user.id,
          source,
          note: note?.trim() || null,
          photo_count: selectedFiles.length,
        })
        .select('id')
        .single()
      if (batchErr || !batchRow) {
        throw new Error(batchErr?.message || 'Could not create batch')
      }
      const taken = selectedFiles.slice()
      selectedBlobsRef.current = [] // ownership transferred to activeBatch
      activeBatch = {
        batchId: batchRow.id,
        source,
        note: note?.trim() || null,
        startedAt: Date.now(),
        totalCount: taken.length,
        pending: taken.map(sf => ({
          fileId: sf.fileId,
          fileName: sf.file.name,
          file: sf.file,
          blobUrl: sf.blobUrl,
        })),
        failed: [],
        onItemSuccess: (entry) => {
          setGalleryItems(prev => {
            if (prev.some(g => g.sessionId === entry.sessionId)) return prev
            return [entry, ...prev]
          })
        },
      }
      setSelectedFiles([])
      setNote('')
      notifyBatchListeners()
      runBatchWorkers(homeId) // fire-and-forget
    } catch (err) {
      console.error('[OwnerSandbox] handleSubmit error', err)
      setSubmitError(err.message || 'Could not start batch.')
    } finally {
      setSubmitting(false)
    }
  }

  function handleRetry(fileId) {
    if (!activeBatch) return
    const failedItem = activeBatch.failed.find(f => f.fileId === fileId)
    if (!failedItem) return
    // Move back to pending
    activeBatch.failed = activeBatch.failed.filter(f => f.fileId !== fileId)
    activeBatch.pending = [...activeBatch.pending, failedItem]
    notifyBatchListeners()
    processOneInBatch(fileId, homeId) // fire-and-forget
  }

  function handleDismissFailed(fileId) {
    if (!activeBatch) return
    const failedItem = activeBatch.failed.find(f => f.fileId === fileId)
    if (failedItem?.blobUrl) {
      try { URL.revokeObjectURL(failedItem.blobUrl) } catch (_) { /* noop */ }
    }
    activeBatch.failed = activeBatch.failed.filter(f => f.fileId !== fileId)
    clearActiveBatchIfIdle()
    notifyBatchListeners()
  }

  // ----- Drill-in ------------------------------------------------------

  async function openDrillIn(sessionId) {
    setDrillInSessionId(sessionId)
    setActiveCorrection(null)
    setEditingCorrectionId(null)
    setMissedMode(false)
    setFilteredOutExpanded(false)
    setPhotoZoom({ scale: 1, x: 0, y: 0 })
    panDragRef.current = { down: false, startX: 0, startY: 0, originX: 0, originY: 0, moved: false }
    pinchRef.current = { active: false, initialDistance: 0, initialScale: 1, initialAnchorX: 0, initialAnchorY: 0, initialOriginX: 0, initialOriginY: 0 }
    loadSessionCorrections(sessionId)
    const galleryItem = galleryItems.find(g => g.sessionId === sessionId)
    if (galleryItem?.items) {
      setDrillInItems(galleryItem.items)
      setDrillInLoading(false)
      return
    }
    setDrillInLoading(true)
    try {
      const items = await fetchDetectionsForSession(sessionId)
      setDrillInItems(items)
      // Cache on the gallery item so re-open is instant.
      setGalleryItems(prev => prev.map(g => g.sessionId === sessionId ? { ...g, items } : g))
    } catch (err) {
      console.error('[OwnerSandbox] drill-in fetch error', err)
      setDrillInItems([])
    } finally {
      setDrillInLoading(false)
    }
  }

  // Pulls detections for one session and maps to the item shape used by
  // the drill-in UI. Extracted so the deep-read button can refresh the
  // single open drill-in without duplicating the SELECT + map logic.
  async function fetchDetectionsForSession(sessionId) {
    const { data, error } = await supabase
      .from('room_photo_detections')
      .select('id, bounding_box, detected_label, refined_label, confidence_band, confidence_score, claude_details, thumbnail_url, frame_index, last_deep_read_at')
      .eq('session_id', sessionId)
      .order('frame_index')
    if (error) throw error
    return (data || []).map(r => ({
      detection_id: r.id,
      label: r.detected_label,
      refined_label: r.refined_label,
      confidence_band: r.confidence_band,
      confidence_score: r.confidence_score,
      bounding_box: r.bounding_box,
      ai_details: r.claude_details,
      frame_index: r.frame_index,
      thumbnail_url: r.thumbnail_url,
      last_deep_read_at: r.last_deep_read_at,
    }))
  }

  function closeDrillIn() {
    setDrillInSessionId(null)
    setDrillInItems([])
    setActiveCorrection(null)
    setEditingCorrectionId(null)
    setDeepReadingDetectionId(null)
    setMissedMode(false)
    setFilteredOutExpanded(false)
    setSessionCorrections([])
    setConfirmDeleteCorrection(null)
    setPhotoZoom({ scale: 1, x: 0, y: 0 })
    panDragRef.current = { down: false, startX: 0, startY: 0, originX: 0, originY: 0, moved: false }
    pinchRef.current = { active: false, initialDistance: 0, initialScale: 1, initialAnchorX: 0, initialAnchorY: 0, initialOriginX: 0, initialOriginY: 0 }
  }

  // Hard-delete a sandbox photo and everything tied to it. Storage file
  // first (best-effort; storage errors don't block the row delete).
  // Session row delete cascades to room_photo_detections and ai_corrections
  // via FK ON DELETE CASCADE. Best-effort decrement of
  // sandbox_batches.photo_count so the batch row stays consistent (the row
  // itself is preserved — source/note has historical value).
  async function handleDeleteSession(target) {
    if (!target || deleting) return
    setDeleting(true)
    // Casavio Design Principle #19 (silent-write guard): do NOT update
    // optimistic UI before the DB confirms the write. supabase-js
    // .delete() returns { error: null } on zero-rows-affected (RLS
    // rejection, row missing, etc.) — see Commit I Bug 4 for the
    // .update() analogue. We snapshot for restoration only as a safety
    // net; the primary discipline is to defer the state change until
    // after the row-count check.
    const backup = galleryItems
    try {
      const parsed = parseStoragePathFromUrl(target.photoUrl)
      if (parsed) {
        try {
          const { data: removed, error: stErr } = await supabase.storage
            .from(parsed.bucket).remove([parsed.path])
          if (stErr) console.warn('[OwnerSandbox] storage delete error (non-fatal)', stErr)
          else if (!removed || removed.length === 0) {
            console.warn('[OwnerSandbox] storage delete affected 0 files (likely already gone); session delete continues')
          }
        } catch (err) {
          console.warn('[OwnerSandbox] storage delete failed (non-fatal)', err)
        }
      }
      const { data: sessDel, error: sessErr } = await supabase
        .from('room_scan_sessions')
        .delete()
        .eq('id', target.sessionId)
        .select('id')
      if (sessErr) throw sessErr
      if (!sessDel || sessDel.length === 0) {
        throw new Error(
          `Delete returned 0 rows. Either RLS rejected the write or the ` +
          `session no longer exists. Session ID: ${target.sessionId}`
        )
      }
      // DELETE confirmed — NOW safe to update local state.
      setGalleryItems(prev => prev.filter(g => g.sessionId !== target.sessionId))
      if (target.batchId) {
        try {
          const { data: b } = await supabase
            .from('sandbox_batches')
            .select('photo_count')
            .eq('id', target.batchId)
            .maybeSingle()
          if (b && (b.photo_count ?? 0) > 0) {
            const { data: updated, error: updErr } = await supabase
              .from('sandbox_batches')
              .update({ photo_count: b.photo_count - 1 })
              .eq('id', target.batchId)
              .select('id')
            if (updErr) console.warn('[OwnerSandbox] batch decrement error', updErr)
            else if (!updated || updated.length === 0) {
              console.warn('[OwnerSandbox] batch photo_count decrement affected 0 rows (non-fatal)', target.batchId)
            }
          }
        } catch (err) {
          console.warn('[OwnerSandbox] batch photo_count decrement failed (non-fatal)', err)
        }
      }
      setConfirmDeleteSession(null)
      // Shift the offset down so the next infinite-scroll page query
      // doesn't skip a row.
      setGalleryOffset(o => Math.max(0, o - 1))
    } catch (err) {
      console.error('[OwnerSandbox] delete failed', err)
      // No restore needed because we never removed the tile, but keep
      // the assignment for clarity if a future refactor moves the
      // optimistic update earlier.
      void backup
      alert(`Couldn't delete this photo: ${err.message || err}. Try refreshing and trying again. If the problem persists, contact support.`)
    } finally {
      setDeleting(false)
    }
  }

  function startCorrectingDetection(itemIndex) {
    if (missedMode) return
    const item = drillInItems[itemIndex]
    if (!item) return
    setActiveCorrection({ mode: 'detection', itemIndex, item })
    setEditingCorrectionId(null)
    // No pre-selected type — forces the user to choose deliberately
    // rather than accidentally logging a 'label' correction on autopilot.
    setCorrectionType('')
    setCorrectionValue('')
    setCorrectionNotes('')
  }

  // Opens the correction form prefilled with an existing correction's values.
  // Save handler detects editingCorrectionId !== null and performs the
  // insert-new + soft-delete-old swap with replaced_by_id linkage.
  //
  // Handles three correction shapes:
  //   1. detection-level (detection_id set, detection in drillInItems)
  //   2. photo-level missed (detection_id NULL, mode='missed' with a
  //      synthetic pin derived from the stored bounding_box)
  //   3. detection-level where detection ISN'T in drillInItems —
  //      typically because the edit was triggered from a context other
  //      than that detection's drill-in. Bails silently.
  function startEditCorrection(correctionRow) {
    if (!correctionRow) return
    // Photo-level (missed) edit
    if (!correctionRow.detection_id) {
      const bb = correctionRow.bounding_box
      const cx = bb ? ((bb.x_min ?? 0) + (bb.x_max ?? 0)) / 2 : 0.5
      const cy = bb ? ((bb.y_min ?? 0) + (bb.y_max ?? 0)) / 2 : 0.5
      setActiveCorrection({ mode: 'missed', missedPin: { x: cx, y: cy } })
      setEditingCorrectionId(correctionRow.id)
      setCorrectionType(correctionRow.correction_type)
      setCorrectionValue(correctionRow.user_value ?? '')
      setCorrectionNotes(correctionRow.notes ?? '')
      return
    }
    const idx = drillInItems.findIndex(it => it.detection_id === correctionRow.detection_id)
    const item = idx >= 0 ? drillInItems[idx] : null
    if (!item) return // detection isn't loaded in this context — bail safely
    setActiveCorrection({ mode: 'detection', itemIndex: idx, item })
    setEditingCorrectionId(correctionRow.id)
    setCorrectionType(correctionRow.correction_type)
    setCorrectionValue(correctionRow.user_value ?? '')
    setCorrectionNotes(correctionRow.notes ?? '')
  }

  // Group-level chip edit: open the per-session drill-in then stash the
  // correction. A useEffect picks it up once drill-in items load and
  // opens the edit form. After save, the drill-in auto-closes so the
  // user returns to the grouped object detail.
  async function openGroupChipEdit(correctionRow) {
    if (!correctionRow?.session_id) return
    setGroupChipEditActive(true)
    setPendingGroupChipEdit(correctionRow)
    await openDrillIn(correctionRow.session_id)
  }

  // Consume the pending edit once the drill-in is loaded for the right
  // session. Photo-level corrections don't need drillInItems; they're
  // handled by the missed-mode branch in startEditCorrection.
  useEffect(() => {
    const pending = pendingGroupChipEdit
    if (!pending) return
    if (drillInSessionId !== pending.session_id) return
    if (drillInLoading) return
    if (pending.detection_id && drillInItems.length === 0) return
    setPendingGroupChipEdit(null)
    startEditCorrection(pending)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drillInSessionId, drillInLoading, drillInItems, pendingGroupChipEdit])

  // ----- Multi-select + grouping --------------------------------------

  function toggleSelectMode() {
    setSelectMode(m => {
      if (m) setSelectedSessionIds(new Set()) // exiting → clear
      return !m
    })
  }

  function toggleSessionSelection(sessionId) {
    setSelectedSessionIds(prev => {
      const next = new Set(prev)
      if (next.has(sessionId)) next.delete(sessionId)
      else next.add(sessionId)
      return next
    })
  }

  // Grouped tiles represent N sessions. Selecting one selects all
  // constituent sessions; deselecting clears them all. Re-grouping into
  // a new sandbox_object will overwrite the existing sandbox_object_id
  // on those sessions — the old sandbox_objects row becomes orphaned
  // (zero linked sessions) but stays in the table per the existing
  // no-auto-cleanup rule. Future maintenance pass can clean up.
  function toggleGroupedTileSelection(sessionIds) {
    setSelectedSessionIds(prev => {
      const next = new Set(prev)
      const allSelected = sessionIds.every(id => next.has(id))
      if (allSelected) sessionIds.forEach(id => next.delete(id))
      else sessionIds.forEach(id => next.add(id))
      return next
    })
  }

  function cancelSelectMode() {
    setSelectMode(false)
    setSelectedSessionIds(new Set())
  }

  // Batch fetch of detections for N sessions. Returns Map<sessionId, rows[]>
  // where rows is sorted by frame_index. Used by the group-modal pre-fill.
  async function fetchDetectionsForSessions(sessionIds) {
    if (!sessionIds.length) return new Map()
    const { data, error } = await supabase
      .from('room_photo_detections')
      .select('id, session_id, refined_label, confidence_score, claude_details')
      .in('session_id', sessionIds)
    if (error) {
      console.error('[OwnerSandbox] fetchDetectionsForSessions error', error)
      return new Map()
    }
    const byId = new Map()
    for (const row of data || []) {
      const arr = byId.get(row.session_id) || []
      arr.push(row)
      byId.set(row.session_id, arr)
    }
    return byId
  }

  // For each AI-extractable field, aggregate values across selected
  // sessions. One "primary value" per session = the highest-confidence
  // detection's value for that field (skips empty values).
  //
  // Returns:
  //   { label: { mode, value, options }, make: ..., ... }
  // where mode is:
  //   'empty'  → no session has a value; render input blank as today
  //   'single' → all contributing sessions agree (case-insensitive trim);
  //              render pre-filled input
  //   'multi'  → distinct values present; render dropdown with counts +
  //              "Type a new X…" escape hatch
  function buildPrefill(detectionsBySession, sessionIds) {
    // Per-field extractors from a single ClaudeDetails-ish object.
    const extractors = {
      label: d => typeof d?.refined_label === 'string' ? d.refined_label : '',
      make: d => typeof d?.brand?.value === 'string' ? d.brand.value : '',
      model: d => typeof d?.model?.value === 'string' ? d.model.value : '',
      serial: d => typeof d?.serial === 'string' ? d.serial : '',
      category: d => typeof d?.category_suggestion === 'string' ? d.category_suggestion : '',
    }
    const result = {}
    for (const field of Object.keys(extractors)) {
      // Gather one value per session (the value from that session's
      // highest-confidence detection, skipping empties).
      const perSessionValues = []
      for (const sid of sessionIds) {
        const rows = detectionsBySession.get(sid) || []
        if (!rows.length) continue
        // Sort by confidence_score DESC; pick the first non-empty value.
        const sorted = [...rows].sort((a, b) => (b.confidence_score ?? 0) - (a.confidence_score ?? 0))
        for (const r of sorted) {
          const v = extractors[field](r.claude_details)
          if (v && v.trim().length > 0) {
            perSessionValues.push(v.trim())
            break
          }
        }
      }
      if (perSessionValues.length === 0) {
        result[field] = { mode: 'empty', value: '', options: [] }
        continue
      }
      // Group by case-insensitive trim to detect agreement.
      const counts = new Map()
      const originalFor = new Map() // keep the first-seen casing as canonical
      for (const v of perSessionValues) {
        const key = v.toLowerCase()
        counts.set(key, (counts.get(key) || 0) + 1)
        if (!originalFor.has(key)) originalFor.set(key, v)
      }
      if (counts.size === 1) {
        const onlyKey = Array.from(counts.keys())[0]
        result[field] = { mode: 'single', value: originalFor.get(onlyKey), options: [] }
      } else {
        const options = Array.from(counts.entries())
          .map(([key, count]) => ({ value: originalFor.get(key), count }))
          .sort((a, b) => b.count - a.count)
        result[field] = { mode: 'multi', value: '', options }
      }
    }
    return result
  }

  // Opens the group modal and runs the pre-fill pass in the background.
  // The form opens immediately blank — pre-fill values land asynchronously
  // (usually <500ms). User can start typing before pre-fill completes; their
  // edits win because we only setGroupForm during the initial pass.
  async function openGroupModal() {
    if (selectedSessionIds.size < 2) return
    setGroupForm({ label: '', make: '', model: '', serial: '', category: '', notes: '' })
    setPrefillModes({})
    setCustomFieldModes(new Set())
    setPrefillLoading(true)
    setGroupModalOpen(true)
    const sessionIds = Array.from(selectedSessionIds)
    try {
      const detectionsBySession = await fetchDetectionsForSessions(sessionIds)
      const prefill = buildPrefill(detectionsBySession, sessionIds)
      setPrefillModes(prefill)
      // Apply single-agreement values to the form. Multi/empty fields stay
      // blank so the dropdown/input behavior takes over in render.
      setGroupForm(f => ({
        ...f,
        label: prefill.label.mode === 'single' ? prefill.label.value : f.label,
        make: prefill.make.mode === 'single' ? prefill.make.value : f.make,
        model: prefill.model.mode === 'single' ? prefill.model.value : f.model,
        serial: prefill.serial.mode === 'single' ? prefill.serial.value : f.serial,
        category: prefill.category.mode === 'single' ? prefill.category.value : f.category,
      }))
    } catch (err) {
      console.error('[OwnerSandbox] openGroupModal prefill failed', err)
    } finally {
      setPrefillLoading(false)
    }
  }

  function toggleCustomField(field) {
    setCustomFieldModes(prev => {
      const next = new Set(prev)
      if (next.has(field)) next.delete(field)
      else next.add(field)
      return next
    })
    // Switching INTO custom mode clears the value so the input starts blank.
    setGroupForm(f => ({ ...f, [field]: '' }))
  }

  function closeGroupModal() {
    if (creatingGroup) return
    setGroupModalOpen(false)
  }

  // Create one sandbox_object row and link every selected session to it.
  // batch_id on the object row comes from the FIRST selected photo for
  // organizational tagging only — cross-batch grouping is supported and
  // the FK on the session rows is what actually defines membership.
  async function handleGroupSubmit() {
    if (creatingGroup) return
    if (selectedSessionIds.size < 2) return
    if (!groupForm.label.trim()) return
    const userId = session?.user?.id
    if (!userId) return
    setCreatingGroup(true)
    try {
      const selectedIds = Array.from(selectedSessionIds)
      const firstSelected = galleryItems.find(g => g.sessionId === selectedIds[0])
      const batchId = firstSelected?.batchId ?? null
      const { data: created, error: insErr } = await supabase
        .from('sandbox_objects')
        .insert({
          batch_id: batchId,
          user_id: userId,
          canonical_label: groupForm.label.trim(),
          canonical_make: groupForm.make.trim() || null,
          canonical_model: groupForm.model.trim() || null,
          canonical_serial: groupForm.serial.trim() || null,
          category: groupForm.category || null,
          notes: groupForm.notes.trim() || null,
          grouping_method: 'manual',
        })
        .select('id')
        .single()
      if (insErr || !created?.id) throw (insErr || new Error('Insert returned no id'))
      const { data: linked, error: updErr } = await supabase
        .from('room_scan_sessions')
        .update({ sandbox_object_id: created.id })
        .in('id', selectedIds)
        .select('id')
      if (updErr) throw updErr
      if (!linked || linked.length === 0) {
        throw new Error(
          `Group created but linking sessions affected 0 rows. RLS or ID mismatch. ` +
          `New group ID: ${created.id}, selected sessions: ${selectedIds.join(',')}`
        )
      }
      // Optimistic local update so the badge appears immediately.
      setGalleryItems(prev => prev.map(g =>
        selectedIds.includes(g.sessionId)
          ? {
              ...g,
              sandboxObjectId: created.id,
              objectCanonicalLabel: groupForm.label.trim() || null,
              objectPrimarySessionId: null,
            }
          : g,
      ))
      setGroupModalOpen(false)
      cancelSelectMode()
    } catch (err) {
      console.error('[OwnerSandbox] group create failed', err)
      alert(`Could not group: ${err.message || err}`)
    } finally {
      setCreatingGroup(false)
    }
  }

  async function openGroupDetail(groupId) {
    if (!groupId) return
    // Closing the drill-in first prevents a stacked overlay flicker.
    closeDrillIn()
    setViewingGroupId(groupId)
    setViewingGroupData(null)
    setViewingGroupLoading(true)
    try {
      const [{ data: object, error: objErr }, { data: sessions, error: sessErr }] = await Promise.all([
        supabase.from('sandbox_objects').select('*').eq('id', groupId).maybeSingle(),
        supabase
          .from('room_scan_sessions')
          .select('id, created_at, source_photo_urls, items_detected, sandbox_source, batch_id, sandbox_object_id, reviewed_at, sandbox_batches(source, note)')
          .eq('sandbox_object_id', groupId)
          .order('created_at', { ascending: true }),
      ])
      if (objErr) throw objErr
      if (sessErr) throw sessErr
      if (!object) throw new Error('Group not found')
      const sessionList = sessions || []
      // Stash the group with no corrections/detections so the section
      // can render its loading spinner while the follow-up queries run.
      setViewingGroupData({ object, sessions: sessionList, corrections: [], detectionsByDetectionId: new Map(), correctionsLoading: true })
      // Follow-up: fetch corrections + detections for the group in one
      // round-trip each. Run in parallel; fail soft (empty list) if
      // either query errors so the rest of the view stays usable.
      const sessionIds = sessionList.map(s => s.id)
      if (sessionIds.length > 0) {
        const [{ data: corrs }, { data: dets }] = await Promise.all([
          supabase
            .from('ai_corrections')
            .select('id, correction_type, field_name, ai_value, user_value, notes, detection_id, session_id, created_at, bounding_box')
            .in('session_id', sessionIds)
            .is('deleted_at', null)
            .order('created_at', { ascending: false }),
          supabase
            .from('room_photo_detections')
            .select('id, session_id, refined_label, detected_label')
            .in('session_id', sessionIds),
        ])
        const detMap = new Map()
        for (const d of dets || []) detMap.set(d.id, d)
        setViewingGroupData(prev => prev && prev.object.id === groupId
          ? { ...prev, corrections: corrs || [], detectionsByDetectionId: detMap, correctionsLoading: false }
          : prev)
      } else {
        setViewingGroupData(prev => prev && prev.object.id === groupId
          ? { ...prev, correctionsLoading: false }
          : prev)
      }
    } catch (err) {
      console.error('[OwnerSandbox] openGroupDetail failed', err)
      alert(`Could not load group: ${err.message || err}`)
      setViewingGroupId(null)
    } finally {
      setViewingGroupLoading(false)
    }
  }

  function closeGroupDetail() {
    setViewingGroupId(null)
    setViewingGroupData(null)
    setConfirmUngroup(false)
    setDragSessionId(null)
    setDragOverIndex(null)
    setPendingGroupChipEdit(null)
    setGroupChipEditActive(false)
  }

  // Ungroup: NULL out the FK on every linked session, then delete the
  // sandbox_objects row. Sessions and their detections/corrections stay
  // intact — only the grouping is removed.
  async function handleUngroup() {
    if (!viewingGroupId || ungrouping) return
    setUngrouping(true)
    try {
      const linkedIds = (viewingGroupData?.sessions || []).map(s => s.id)
      if (linkedIds.length > 0) {
        const { data: cleared, error: updErr } = await supabase
          .from('room_scan_sessions')
          .update({ sandbox_object_id: null })
          .in('id', linkedIds)
          .select('id')
        if (updErr) throw updErr
        if (!cleared || cleared.length === 0) {
          throw new Error(
            `Ungroup link-clear affected 0 rows. RLS or sessions vanished. ` +
            `Group ID: ${viewingGroupId}, expected ${linkedIds.length} rows.`
          )
        }
      }
      const { data: deleted, error: delErr } = await supabase
        .from('sandbox_objects')
        .delete()
        .eq('id', viewingGroupId)
        .select('id')
      if (delErr) throw delErr
      if (!deleted || deleted.length === 0) {
        throw new Error(
          `Ungroup DELETE returned 0 rows. RLS or group missing. ID: ${viewingGroupId}`
        )
      }
      // Optimistic gallery update so badges clear on next view.
      setGalleryItems(prev => prev.map(g =>
        linkedIds.includes(g.sessionId)
          ? { ...g, sandboxObjectId: null, objectCanonicalLabel: null, objectPrimarySessionId: null }
          : g,
      ))
      closeGroupDetail()
    } catch (err) {
      console.error('[OwnerSandbox] ungroup failed', err)
      alert(`Could not ungroup: ${err.message || err}`)
    } finally {
      setUngrouping(false)
    }
  }

  // Collapse a flat list of galleryItem-shaped sessions into the
  // displayedTiles array. Each output element is either:
  //   { kind: 'photo', sortKey, g } — one ungrouped session
  //   { kind: 'group', sortKey, objectId, sessions, representative, canonicalLabel, primarySessionId }
  // Sorted by sortKey DESC. The representative is the user-selected
  // primary if present and loaded; otherwise most-recent session.
  function buildDisplayedTiles(items) {
    const groupMap = new Map()
    const photoTiles = []
    for (const g of items) {
      if (g.sandboxObjectId) {
        const entry = groupMap.get(g.sandboxObjectId) || {
          objectId: g.sandboxObjectId,
          sessions: [],
          maxCreated: '',
          canonicalLabel: g.objectCanonicalLabel || null,
          primarySessionId: g.objectPrimarySessionId || null,
        }
        entry.sessions.push(g)
        if (g.createdAt > entry.maxCreated) entry.maxCreated = g.createdAt
        // Latest non-null values win (shouldn't conflict but defensive).
        if (g.objectCanonicalLabel) entry.canonicalLabel = g.objectCanonicalLabel
        if (g.objectPrimarySessionId) entry.primarySessionId = g.objectPrimarySessionId
        groupMap.set(g.sandboxObjectId, entry)
      } else {
        photoTiles.push({ kind: 'photo', sortKey: g.createdAt, g })
      }
    }
    const groupTiles = Array.from(groupMap.values()).map(entry => {
      const sorted = [...entry.sessions].sort((a, b) => b.createdAt.localeCompare(a.createdAt))
      const rep = pickRepresentative(sorted, entry.primarySessionId)
      return {
        kind: 'group',
        sortKey: entry.maxCreated,
        objectId: entry.objectId,
        sessions: sorted,
        representative: rep,
        canonicalLabel: entry.canonicalLabel,
        primarySessionId: entry.primarySessionId,
      }
    })
    return [...photoTiles, ...groupTiles].sort((a, b) => b.sortKey.localeCompare(a.sortKey))
  }

  // Top user-visible label for an ungrouped photo tile: highest-confidence
  // should_log=true detection's refined_label. Returns null when items
  // aren't loaded yet OR no Casavio item passes the filter.
  function topKeptDetectionLabel(g) {
    if (!Array.isArray(g.items) || g.items.length === 0) return null
    const kept = g.items.filter(it => it?.ai_details?.should_log !== false)
    if (kept.length === 0) return null
    const sorted = [...kept].sort((a, b) => (b.confidence_score ?? 0) - (a.confidence_score ?? 0))
    const top = sorted[0]
    return top?.refined_label || top?.label || null
  }

  // Pick the representative session for a grouped tile. If the group has
  // a user-selected primary AND that session is in the loaded set, use
  // it. Otherwise fall back to the most-recent session (created_at DESC).
  function pickRepresentative(sessions, primarySessionId) {
    if (primarySessionId) {
      const found = sessions.find(s => s.sessionId === primarySessionId)
      if (found) return found
    }
    const sorted = [...sessions].sort((a, b) => b.createdAt.localeCompare(a.createdAt))
    return sorted[0]
  }

  // Fetch the user's reviewed sandbox sessions in one shot, capped at
  // 100. Same shape as galleryItems so the existing tile renderer can
  // share the code path.
  async function loadReviewedItems() {
    const userId = session?.user?.id
    if (!userId) return
    setReviewedLoading(true)
    try {
      const REVIEWED_CAP = 100
      const { data, error } = await supabase
        .from('room_scan_sessions')
        .select(`
          id, created_at, source_photo_urls, items_detected, status,
          sandbox_source, sandbox_note, batch_id, sandbox_object_id, reviewed_at,
          sandbox_batches ( source, note ),
          sandbox_objects!sandbox_object_id ( canonical_label, primary_session_id )
        `)
        .eq('capture_mode', 'sandbox')
        .eq('user_id', userId)
        .not('reviewed_at', 'is', null)
        .order('reviewed_at', { ascending: false })
        .limit(REVIEWED_CAP + 1)
      if (error) throw error
      const rows = data || []
      // Best-effort correction counts; reuse the same active-corrections
      // filter as the main gallery so badges match.
      let countMap = new Map()
      if (rows.length > 0) {
        const ids = rows.map(r => r.id)
        const { data: corrs } = await supabase
          .from('ai_corrections')
          .select('session_id')
          .in('session_id', ids)
          .is('deleted_at', null)
        for (const c of corrs || []) {
          countMap.set(c.session_id, (countMap.get(c.session_id) || 0) + 1)
        }
      }
      const mapped = rows.slice(0, REVIEWED_CAP).map(s => ({
        sessionId: s.id,
        createdAt: s.created_at,
        photoUrl: s.source_photo_urls?.[0] || null,
        itemsDetected: s.items_detected ?? 0,
        correctionsCount: countMap.get(s.id) || 0,
        status: s.status,
        batchSource: s.sandbox_batches?.source || null,
        batchNote: s.sandbox_batches?.note || null,
        sandboxSource: s.sandbox_source || null,
        sandboxNote: s.sandbox_note || null,
        batchId: s.batch_id || null,
        sandboxObjectId: s.sandbox_object_id || null,
        objectCanonicalLabel: s.sandbox_objects?.canonical_label || null,
        objectPrimarySessionId: s.sandbox_objects?.primary_session_id || null,
        reviewedAt: s.reviewed_at,
        items: null,
      }))
      setReviewedItems(mapped)
      setReviewedHitCap(rows.length > REVIEWED_CAP)
    } catch (err) {
      console.error('[OwnerSandbox] loadReviewedItems error', err)
      setReviewedItems([])
    } finally {
      setReviewedLoading(false)
    }
  }

  function toggleReviewedExpanded() {
    setReviewedExpanded(v => {
      const next = !v
      if (next) loadReviewedItems()
      return next
    })
  }

  // Per-session mark-reviewed. Toggle direction inferred from current
  // reviewedAt value when not supplied.
  async function setSessionReviewedAt(sessionId, newValue) {
    if (markingReviewed) return
    setMarkingReviewed(true)
    try {
      const { data, error } = await supabase
        .from('room_scan_sessions')
        .update({ reviewed_at: newValue })
        .eq('id', sessionId)
        .select('id')
      if (error) throw error
      if (!data || data.length === 0) {
        throw new Error(
          `Review-state update returned 0 rows. RLS or session missing. ID: ${sessionId}`
        )
      }
      // Update galleryItems entry (if loaded).
      setGalleryItems(prev => prev.map(g =>
        g.sessionId === sessionId ? { ...g, reviewedAt: newValue } : g,
      ))
      // Update reviewedItems entry. Remove on unreview, prepend on review.
      if (newValue === null) {
        setReviewedItems(prev => prev.filter(g => g.sessionId !== sessionId))
      } else {
        setReviewedItems(prev => {
          const exists = prev.some(g => g.sessionId === sessionId)
          if (exists) return prev.map(g => g.sessionId === sessionId ? { ...g, reviewedAt: newValue } : g)
          // Find the session in galleryItems to prepend; if not loaded
          // (rare), let the next reviewed expand fetch find it.
          const from = galleryItems.find(g => g.sessionId === sessionId)
          if (!from) return prev
          return [{ ...from, reviewedAt: newValue }, ...prev]
        })
      }
    } catch (err) {
      console.error('[OwnerSandbox] setSessionReviewedAt failed', err)
      alert(`Could not update review state: ${err.message || err}`)
    } finally {
      setMarkingReviewed(false)
    }
  }

  // Bulk mark for all sessions in a group. newValue = ISO string or null.
  async function setGroupReviewedAt(groupId, newValue) {
    if (markingReviewed) return
    setMarkingReviewed(true)
    try {
      const { data, error } = await supabase
        .from('room_scan_sessions')
        .update({ reviewed_at: newValue })
        .eq('sandbox_object_id', groupId)
        .select('id')
      if (error) throw error
      if (!data || data.length === 0) {
        // Empty groups are legal in theory (after all photos deleted) but
        // the UI flow never reaches this branch with zero linked sessions,
        // so 0 rows here implies RLS rejection.
        throw new Error(
          `Bulk review-state update returned 0 rows. RLS or empty group. ID: ${groupId}`
        )
      }
      setGalleryItems(prev => prev.map(g =>
        g.sandboxObjectId === groupId ? { ...g, reviewedAt: newValue } : g,
      ))
      if (newValue === null) {
        setReviewedItems(prev => prev.filter(g => g.sandboxObjectId !== groupId))
      } else {
        // Reviewed list will refresh on next expand; for now just patch
        // any already-loaded entries.
        setReviewedItems(prev => prev.map(g =>
          g.sandboxObjectId === groupId ? { ...g, reviewedAt: newValue } : g,
        ))
      }
    } catch (err) {
      console.error('[OwnerSandbox] setGroupReviewedAt failed', err)
      alert(`Could not update review state: ${err.message || err}`)
    } finally {
      setMarkingReviewed(false)
    }
  }

  // Update primary photo for a group. NULL reverts to most-recent fallback.
  async function setGroupPrimarySession(groupId, sessionId) {
    try {
      const { data, error } = await supabase
        .from('sandbox_objects')
        .update({ primary_session_id: sessionId })
        .eq('id', groupId)
        .select('id')
      if (error) throw error
      if (!data || data.length === 0) {
        throw new Error(
          `Primary-photo update returned 0 rows. RLS or group missing. ID: ${groupId}`
        )
      }
      // Optimistic update for any cached gallery rows + open group view.
      setGalleryItems(prev => prev.map(g =>
        g.sandboxObjectId === groupId ? { ...g, objectPrimarySessionId: sessionId } : g,
      ))
      setReviewedItems(prev => prev.map(g =>
        g.sandboxObjectId === groupId ? { ...g, objectPrimarySessionId: sessionId } : g,
      ))
      setViewingGroupData(prev => prev && prev.object.id === groupId
        ? { ...prev, object: { ...prev.object, primary_session_id: sessionId } }
        : prev)
    } catch (err) {
      console.error('[OwnerSandbox] setGroupPrimarySession failed', err)
      alert(`Could not update primary photo: ${err.message || err}`)
    }
  }

  // Calls the deep-read EF for one detection, refetches the drill-in on
  // success so the merged claude_details + new last_deep_read_at hit the
  // UI. Fire-and-forget per the spec (no confirm — additive fill-nulls).
  async function handleDeepRead(detectionId) {
    if (!detectionId || deepReadingDetectionId) return
    setDeepReadingDetectionId(detectionId)
    try {
      const galleryItem = galleryItems.find(g => g.sessionId === drillInSessionId)
      const { data, error } = await supabase.functions.invoke('deep-read-detection', {
        body: { detection_id: detectionId, home_id: homeId ?? null },
      })
      if (error) throw new Error(error.message || 'Edge Function error')
      if (!data || data.error) throw new Error(data?.error || 'Deep read failed')
      // Refresh items for the open drill-in so the new fields appear.
      if (drillInSessionId) {
        const refreshed = await fetchDetectionsForSession(drillInSessionId)
        setDrillInItems(refreshed)
        setGalleryItems(prev => prev.map(g =>
          g.sessionId === drillInSessionId ? { ...g, items: refreshed } : g,
        ))
      }
      // galleryItem is unused beyond the refresh path; intentionally left
      // bound for symmetry with sibling handlers that need it.
      void galleryItem
    } catch (err) {
      console.error('[OwnerSandbox] deep-read failed', err)
      alert(`Deep read failed: ${err.message || err}`)
    } finally {
      setDeepReadingDetectionId(null)
    }
  }

  // Soft-delete: UPDATE deleted_at = now() on the row. Preserves history;
  // active-corrections-only read paths will hide it. Patches every cached
  // surface that lists corrections (drill-in, gallery badge, reviewed
  // tiles, group detail) so no stale chip lingers.
  async function performSoftDeleteCorrection(correctionId) {
    if (!correctionId) return
    try {
      // Find the affected session so we can decrement the right gallery
      // badge — the correction's session_id may not equal drillInSessionId
      // when the delete was triggered from the group-level surface.
      const fromSession =
        sessionCorrections.find(c => c.id === correctionId)?.session_id ??
        viewingGroupData?.corrections?.find(c => c.id === correctionId)?.session_id ??
        drillInSessionId
      // .select() so we get back the affected rows. Without it,
      // supabase-js returns { error: null } even when RLS or an ID
      // mismatch causes the UPDATE to affect 0 rows — and the optimistic
      // UI proceeds as if it succeeded while deleted_at stays NULL in
      // the DB. With .select() we can detect zero-rows and abort.
      const { data, error } = await supabase
        .from('ai_corrections')
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', correctionId)
        .select('id')
      if (error) throw error
      if (!data || data.length === 0) {
        throw new Error('No rows affected — the correction may have been deleted by another session, or you may not have permission.')
      }
      setSessionCorrections(prev => prev.filter(c => c.id !== correctionId))
      setGalleryItems(prev => prev.map(g =>
        g.sessionId === fromSession
          ? { ...g, correctionsCount: Math.max(0, (g.correctionsCount ?? 0) - 1) }
          : g,
      ))
      setReviewedItems(prev => prev.map(g =>
        g.sessionId === fromSession
          ? { ...g, correctionsCount: Math.max(0, (g.correctionsCount ?? 0) - 1) }
          : g,
      ))
      // Patch the open grouped-detail view if relevant.
      setViewingGroupData(prev => prev
        ? { ...prev, corrections: (prev.corrections || []).filter(c => c.id !== correctionId) }
        : prev)
      loadRecentCorrections()
      setConfirmDeleteCorrection(null)
    } catch (err) {
      console.error('[OwnerSandbox] soft-delete correction failed', err)
      alert(`Could not remove correction: ${err.message || err}`)
    }
  }

  // ----- Photo zoom / pan handlers ------------------------------------

  function clampPan(x, y, scale) {
    const rect = photoViewportRef.current?.getBoundingClientRect()
    if (!rect) return { x, y }
    const maxX = (rect.width * (scale - 1)) / 2
    const maxY = (rect.height * (scale - 1)) / 2
    return {
      x: Math.max(-maxX, Math.min(maxX, x)),
      y: Math.max(-maxY, Math.min(maxY, y)),
    }
  }

  function handlePhotoPointerDown(e) {
    // Only intercept primary pointer button (left mouse / single touch).
    if (e.button !== undefined && e.button !== 0) return
    // Pinch owns the gesture once it's underway — don't start a pan that
    // would race with the pinch math.
    if (pinchRef.current.active) return
    panDragRef.current = {
      down: true,
      startX: e.clientX,
      startY: e.clientY,
      originX: photoZoom.x,
      originY: photoZoom.y,
      moved: false,
    }
    // Capture so we keep getting pointermove even if pointer leaves the
    // element (e.g. mouse drags off the photo).
    try { e.currentTarget.setPointerCapture(e.pointerId) } catch (_) { /* noop */ }
  }

  function handlePhotoPointerMove(e) {
    if (pinchRef.current.active) return
    if (!panDragRef.current.down) return
    if (photoZoom.scale === 1) return // no pan when not zoomed
    const dx = e.clientX - panDragRef.current.startX
    const dy = e.clientY - panDragRef.current.startY
    if (!panDragRef.current.moved && (Math.abs(dx) > 5 || Math.abs(dy) > 5)) {
      panDragRef.current.moved = true
    }
    if (!panDragRef.current.moved) return
    e.preventDefault()
    const bounded = clampPan(
      panDragRef.current.originX + dx,
      panDragRef.current.originY + dy,
      photoZoom.scale,
    )
    setPhotoZoom(z => ({ ...z, x: bounded.x, y: bounded.y }))
  }

  function handlePhotoPointerUp(e) {
    panDragRef.current.down = false
    try { e.currentTarget.releasePointerCapture(e.pointerId) } catch (_) { /* noop */ }
  }

  // Two-finger pinch zoom. Inline handlers work because the container has
  // touch-action: none, blocking the browser's default page-zoom gesture.
  // Math: anchor the inner-coord point that was under the initial midpoint
  // to wherever the current midpoint is now, applying the new scale.
  function handlePhotoTouchStart(e) {
    if (e.touches.length === 2) {
      const t1 = e.touches[0]
      const t2 = e.touches[1]
      const dx = t2.clientX - t1.clientX
      const dy = t2.clientY - t1.clientY
      pinchRef.current = {
        active: true,
        initialDistance: Math.hypot(dx, dy) || 1,
        initialScale: photoZoom.scale,
        initialAnchorX: (t1.clientX + t2.clientX) / 2,
        initialAnchorY: (t1.clientY + t2.clientY) / 2,
        initialOriginX: photoZoom.x,
        initialOriginY: photoZoom.y,
      }
      // Cancel any in-progress single-finger pan AND suppress the
      // synthesized tap that would otherwise fire on touchend.
      panDragRef.current.down = false
      panDragRef.current.moved = true
    }
  }

  function handlePhotoTouchMove(e) {
    if (!pinchRef.current.active) return
    if (e.touches.length < 2) return
    if (!photoViewportRef.current) return
    const t1 = e.touches[0]
    const t2 = e.touches[1]
    const currentDistance = Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY) || 1
    const ratio = currentDistance / pinchRef.current.initialDistance
    let newScale = pinchRef.current.initialScale * ratio
    newScale = Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, newScale))
    const rect = photoViewportRef.current.getBoundingClientRect()
    const cx = rect.width / 2
    const cy = rect.height / 2
    const midNowX = (t1.clientX + t2.clientX) / 2 - rect.left
    const midNowY = (t1.clientY + t2.clientY) / 2 - rect.top
    const midThenX = pinchRef.current.initialAnchorX - rect.left
    const midThenY = pinchRef.current.initialAnchorY - rect.top
    const initialScale = pinchRef.current.initialScale
    const newX = midNowX - cx - ((midThenX - cx - pinchRef.current.initialOriginX) / initialScale) * newScale
    const newY = midNowY - cy - ((midThenY - cy - pinchRef.current.initialOriginY) / initialScale) * newScale
    const maxX = (rect.width * (newScale - 1)) / 2
    const maxY = (rect.height * (newScale - 1)) / 2
    setPhotoZoom({
      scale: newScale,
      x: Math.max(-maxX, Math.min(maxX, newX)),
      y: Math.max(-maxY, Math.min(maxY, newY)),
    })
  }

  function handlePhotoTouchEnd(e) {
    if (!pinchRef.current.active) return
    if (e.touches.length < 2) {
      pinchRef.current.active = false
      // Below the snap threshold, release back to natural 1x with no pan.
      setPhotoZoom(z => (z.scale < ZOOM_PINCH_SNAP_THRESHOLD ? { scale: 1, x: 0, y: 0 } : z))
    }
  }

  // Mouse wheel zoom centered on cursor. Bound via useEffect with
  // { passive: false } because React 17+ makes onWheel passive by default,
  // which would silently no-op preventDefault and let the page scroll.
  useEffect(() => {
    if (drillInSessionId === null) return
    const el = photoViewportRef.current
    if (!el) return
    function onWheel(e) {
      e.preventDefault()
      if (!photoViewportRef.current) return
      const rect = photoViewportRef.current.getBoundingClientRect()
      const anchorX = e.clientX - rect.left
      const anchorY = e.clientY - rect.top
      const cx = rect.width / 2
      const cy = rect.height / 2
      // Negate because deltaY > 0 is scroll-down which conventionally
      // zooms OUT in image viewers (and vice versa).
      const step = -Math.sign(e.deltaY) * ZOOM_WHEEL_STEP
      setPhotoZoom(z => {
        const newScale = Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, z.scale + step))
        if (newScale <= ZOOM_MIN) return { scale: 1, x: 0, y: 0 }
        const newX = anchorX - cx - (anchorX - cx - z.x) * (newScale / z.scale)
        const newY = anchorY - cy - (anchorY - cy - z.y) * (newScale / z.scale)
        const maxX = (rect.width * (newScale - 1)) / 2
        const maxY = (rect.height * (newScale - 1)) / 2
        return {
          scale: newScale,
          x: Math.max(-maxX, Math.min(maxX, newX)),
          y: Math.max(-maxY, Math.min(maxY, newY)),
        }
      })
    }
    el.addEventListener('wheel', onWheel, { passive: false })
    return () => el.removeEventListener('wheel', onWheel)
  }, [drillInSessionId])

  function handlePhotoTap(e) {
    // Drag-suppression: if the pointer moved more than the threshold during
    // this gesture, swallow the click and reset for the next one.
    if (panDragRef.current.moved) {
      panDragRef.current.moved = false
      return
    }
    // Defense-in-depth: never fire tap-toggle while a pinch is live.
    if (pinchRef.current.active) return
    // Missed-mode owns the click — place a pin at the click point.
    if (missedMode && imgRef.current) {
      const rect = imgRef.current.getBoundingClientRect()
      const x = (e.clientX - rect.left) / rect.width
      const y = (e.clientY - rect.top) / rect.height
      if (x < 0 || x > 1 || y < 0 || y > 1) return
      setActiveCorrection({ mode: 'missed', missedPin: { x, y } })
      setEditingCorrectionId(null)
      setCorrectionType('label')
      setCorrectionValue('')
      setCorrectionNotes('')
      setMissedMode(false)
      return
    }
    // Otherwise: toggle zoom. Zoom-in centers on the click point.
    if (photoZoom.scale === 1 && photoViewportRef.current) {
      const rect = photoViewportRef.current.getBoundingClientRect()
      const clickX = e.clientX - rect.left
      const clickY = e.clientY - rect.top
      const cx = rect.width / 2
      const cy = rect.height / 2
      const s = 2
      const tx = -(clickX - cx) * s
      const ty = -(clickY - cy) * s
      const bounded = clampPan(tx, ty, s)
      setPhotoZoom({ scale: s, x: bounded.x, y: bounded.y })
    } else {
      setPhotoZoom({ scale: 1, x: 0, y: 0 })
    }
  }

  async function handleSaveCorrection() {
    if (!activeCorrection || drillInSessionId === null) return
    const userId = session?.user?.id
    if (!userId) return
    const galleryItem = galleryItems.find(g => g.sessionId === drillInSessionId)
    const isMissed = activeCorrection.mode === 'missed'
    const typeDef = CORRECTION_TYPES.find(t => t.value === correctionType)
    const inputKind = isMissed ? 'text' : (typeDef?.inputKind ?? 'text')
    if (inputKind !== 'none' && !correctionValue.trim()) return
    const isEdit = editingCorrectionId !== null
    setSavingCorrection(true)
    try {
      const row = {
        session_id: drillInSessionId,
        detection_id: isMissed ? null : (activeCorrection.item?.detection_id ?? null),
        correction_type: isMissed ? 'missed' : correctionType,
        field_name: isMissed ? null : (typeDef?.fieldName ?? null),
        ai_value: isMissed ? null : aiValueForType(activeCorrection.item, correctionType),
        user_value: inputKind === 'none' ? null : correctionValue.trim(),
        notes: correctionNotes.trim() || null,
        source: 'sandbox',
        user_id: userId,
        context_source: galleryItem?.batchSource || galleryItem?.sandboxSource || null,
        bounding_box: isMissed
          ? {
              x_min: Math.max(0, activeCorrection.missedPin.x - 0.05),
              y_min: Math.max(0, activeCorrection.missedPin.y - 0.05),
              x_max: Math.min(1, activeCorrection.missedPin.x + 0.05),
              y_max: Math.min(1, activeCorrection.missedPin.y + 0.05),
            }
          : null,
      }
      // Insert the new (or replacement) row and capture its id.
      const { data: inserted, error: insErr } = await supabase
        .from('ai_corrections')
        .insert(row)
        .select('id')
        .single()
      if (insErr || !inserted?.id) throw (insErr || new Error('Insert returned no id'))
      // If editing, soft-delete the old row and link replaced_by to the
      // new id. Per spec: if insert succeeded, we proceed with the update;
      // any failure here logs but doesn't roll back (the new row stays as
      // useful training data either way).
      if (isEdit) {
        try {
          const { data: oldRow, error: updErr } = await supabase
            .from('ai_corrections')
            .update({ deleted_at: new Date().toISOString(), replaced_by_id: inserted.id })
            .eq('id', editingCorrectionId)
            .select('id')
          if (updErr) console.error('[OwnerSandbox] edit soft-delete failed', updErr)
          else if (!oldRow || oldRow.length === 0) {
            // Worst-case silent failure: new row exists AND old row is
            // still active → DUPLICATE corrections in training data. Log
            // at error level so it surfaces in monitoring. Per spec we
            // don't roll back the insert (the new row is still useful
            // training data), but a maintenance pass may need to
            // soft-delete the old row out-of-band.
            console.error(
              '[OwnerSandbox] edit soft-delete affected 0 rows — duplicate corrections will exist',
              { editingCorrectionId, newId: inserted.id },
            )
          }
        } catch (err) {
          console.error('[OwnerSandbox] edit soft-delete exception', err)
        }
      }
      // Update local sessionCorrections immediately so chips reflect the
      // new state without waiting for refetch.
      await loadSessionCorrections(drillInSessionId)
      // Active-corrections count: +1 on create, unchanged on edit.
      if (!isEdit) {
        setGalleryItems(prev => prev.map(g =>
          g.sessionId === drillInSessionId
            ? { ...g, correctionsCount: (g.correctionsCount ?? 0) + 1 }
            : g,
        ))
      }
      // Refresh group-level corrections too if the group detail is open.
      // Refetch is simplest and the row count is small enough to ignore.
      if (viewingGroupId && viewingGroupData) {
        const sessionIds = (viewingGroupData.sessions || []).map(s => s.id)
        if (sessionIds.length > 0) {
          const { data: corrs } = await supabase
            .from('ai_corrections')
            .select('id, correction_type, field_name, ai_value, user_value, notes, detection_id, session_id, created_at, bounding_box')
            .in('session_id', sessionIds)
            .is('deleted_at', null)
            .order('created_at', { ascending: false })
          setViewingGroupData(prev => prev && prev.object.id === viewingGroupId
            ? { ...prev, corrections: corrs || [] }
            : prev)
        }
      }
      setActiveCorrection(null)
      setEditingCorrectionId(null)
      loadRecentCorrections()
      // If this edit was triggered from a group-level chip, close the
      // drill-in so the user returns to the grouped object detail.
      if (groupChipEditActive) {
        setGroupChipEditActive(false)
        closeDrillIn()
      }
    } catch (err) {
      console.error('[OwnerSandbox] save correction error', err)
      alert(`Could not save correction: ${err.message || err}`)
    } finally {
      setSavingCorrection(false)
    }
  }

  // ----- Layout primitives ---------------------------------------------

  const wrap = { maxWidth: 1100, margin: '0 auto', padding: '16px 14px 60px', fontFamily: fs }
  const cardStyle = { background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: '14px 16px', marginBottom: 14 }
  const labelStyle = { fontSize: 10, color: C.muted, letterSpacing: 1.5, textTransform: 'uppercase', fontWeight: 700, marginBottom: 6, display: 'block' }
  const inputStyle = { width: '100%', background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8, padding: '10px 12px', fontSize: 14, fontFamily: fs, color: C.brown, boxSizing: 'border-box', outline: 'none' }
  const selectStyle = {
    ...inputStyle,
    appearance: 'none',
    WebkitAppearance: 'none',
    MozAppearance: 'none',
    paddingRight: 36,
    backgroundImage: SELECT_CHEVRON_BG,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 12px center',
    cursor: 'pointer',
  }
  const primaryBtn = { background: `linear-gradient(180deg, ${C.amberLight} 0%, ${C.amber} 100%)`, color: '#fff', border: 'none', borderRadius: 10, padding: '12px 22px', fontFamily: f, fontSize: 14, fontWeight: 700, cursor: 'pointer', boxShadow: '0 2px 6px rgba(184,114,31,0.25)' }
  const secondaryBtn = { background: '#fff', color: C.brown, border: `1px solid ${C.border}`, borderRadius: 10, padding: '10px 18px', fontFamily: fs, fontSize: 13, fontWeight: 600, cursor: 'pointer' }
  const linkBtn = { background: 'transparent', border: 'none', color: C.amber, cursor: 'pointer', fontFamily: fs, fontSize: 14, padding: 0, display: 'inline-flex', alignItems: 'center', gap: 6 }

  // ----- Render: upload card -------------------------------------------

  function renderUploadCard() {
    const atCap = selectedFiles.length >= MAX_BATCH_SIZE
    const hasFiles = selectedFiles.length > 0
    return (
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        style={{
          ...cardStyle,
          border: isDraggingOver ? `2px dashed ${C.amber}` : cardStyle.border,
          background: isDraggingOver ? '#FBF3E8' : cardStyle.background,
          transition: 'background 120ms ease-out, border-color 120ms ease-out',
        }}
      >
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'flex-end', marginBottom: hasFiles ? 12 : 0 }}>
          <div style={{ minWidth: 160 }}>
            <label style={labelStyle}>Source</label>
            <select value={source} onChange={e => setSource(e.target.value)} className="os-sandbox-select" style={selectStyle}>
              {SOURCE_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>

          <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" style={{ display: 'none' }} onChange={handleFilesPicked} />
          <input ref={libraryInputRef} type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={handleFilesPicked} />

          <button
            onClick={() => cameraInputRef.current?.click()}
            disabled={atCap}
            style={{ ...secondaryBtn, display: 'inline-flex', alignItems: 'center', gap: 8, opacity: atCap ? 0.5 : 1, cursor: atCap ? 'not-allowed' : 'pointer' }}
          >
            <Camera size={14} /> Take Photo
          </button>
          <button
            onClick={() => libraryInputRef.current?.click()}
            disabled={atCap}
            style={{ ...secondaryBtn, display: 'inline-flex', alignItems: 'center', gap: 8, opacity: atCap ? 0.5 : 1, cursor: atCap ? 'not-allowed' : 'pointer' }}
          >
            <ImageIcon size={14} /> Choose Files
          </button>
          <div style={{ flex: 1, minWidth: 160, fontSize: 11, color: C.muted, fontStyle: 'italic', alignSelf: 'center' }}>
            {isDraggingOver
              ? 'Release to add these photos'
              : hasFiles
                ? `${selectedFiles.length} of ${MAX_BATCH_SIZE} selected · drop or paste to add more`
                : 'or drop / paste'}
          </div>
        </div>

        {hasFiles && (
          <>
            <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 8, marginBottom: 10 }}>
              {selectedFiles.slice(0, 10).map(sf => (
                <div key={sf.fileId} style={{ position: 'relative', flexShrink: 0 }}>
                  <img
                    src={sf.blobUrl}
                    alt=""
                    style={{ width: 52, height: 52, objectFit: 'cover', borderRadius: 6, background: C.bg }}
                  />
                  <button
                    onClick={() => removeSelectedFile(sf.fileId)}
                    aria-label="Remove"
                    style={{
                      position: 'absolute', top: -5, right: -5,
                      width: 18, height: 18, borderRadius: '50%',
                      background: '#fff', border: `1px solid ${C.border}`,
                      cursor: 'pointer', padding: 0, lineHeight: 0,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}
                  >
                    <X size={10} color={C.muted} />
                  </button>
                </div>
              ))}
              {selectedFiles.length > 10 && (
                <div style={{ flexShrink: 0, alignSelf: 'center', fontSize: 12, color: C.muted, paddingLeft: 6 }}>
                  + {selectedFiles.length - 10} more
                </div>
              )}
            </div>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'flex-end' }}>
              <div style={{ flex: 1, minWidth: 200 }}>
                <label style={labelStyle}>Note (optional)</label>
                <input
                  type="text"
                  value={note}
                  onChange={e => setNote(e.target.value)}
                  placeholder="e.g. Chris's Tiburon listing May 28"
                  style={inputStyle}
                />
              </div>
              <button
                onClick={handleSubmit}
                disabled={!homeId || submitting}
                style={{
                  ...primaryBtn,
                  opacity: (!homeId || submitting) ? 0.5 : 1,
                  cursor: (!homeId || submitting) ? 'not-allowed' : 'pointer',
                }}
              >
                {submitting ? 'Starting…' : `Analyze ${selectedFiles.length} photo${selectedFiles.length === 1 ? '' : 's'}`}
              </button>
            </div>
            {!homeId && (
              <div style={{ fontSize: 11, color: C.muted, marginTop: 8 }}>Loading your home id…</div>
            )}
            {submitError && (
              <div style={{ color: C.danger, fontSize: 12, marginTop: 8 }}>{submitError}</div>
            )}
          </>
        )}
      </div>
    )
  }

  // ----- Render: in-flight pill ----------------------------------------

  function renderInFlightPill() {
    if (!activeBatch) return null
    if (activeBatch.pending.length === 0 && activeBatch.failed.length === 0) return null
    const total = activeBatch.totalCount
    const remaining = activeBatch.pending.length
    const completed = total - remaining - activeBatch.failed.length
    return (
      <div style={{
        background: C.amber, color: '#fff',
        padding: '8px 16px', borderRadius: 999,
        fontFamily: fs, fontSize: 13, fontWeight: 700,
        display: 'inline-flex', alignItems: 'center', gap: 8,
        marginBottom: 12,
        boxShadow: '0 2px 6px rgba(184,114,31,0.25)',
      }}>
        {remaining > 0 && (
          <Loader size={14} color="#fff" strokeWidth={2.5} style={{ animation: 'ownersandbox-spin 1s linear infinite' }} />
        )}
        {remaining > 0
          ? `Processing ${completed + 1} of ${total}${activeBatch.failed.length > 0 ? ` · ${activeBatch.failed.length} failed` : ''}`
          : `${total - activeBatch.failed.length} of ${total} done · ${activeBatch.failed.length} failed`}
      </div>
    )
  }

  // ----- Render: gallery -----------------------------------------------

  function renderGallery() {
    // Build tiles from the UNION of galleryItems + reviewedItems so a
    // group's full membership is visible regardless of which pool any
    // individual session was loaded into. Without this union, a partially
    // reviewed group ends up with sessions split across both pools, and
    // the Active section sees only the unreviewed sessions while Reviewed
    // sees only the reviewed ones — both ending up with the wrong photo
    // count and wrong section placement (Bugs 2 + 3).
    //
    // Group-aware review rule (per spec): a group goes to Reviewed only
    // when ALL its sessions have reviewed_at set. Any unreviewed session
    // keeps the whole group in Active.
    //
    // Pagination note: galleryOffset still counts raw session rows from
    // loadGalleryPage's range() call. Tiles per page may be fewer than
    // the page size (collapse + reviewed filter); dedup-by-sessionId in
    // loadGalleryPage handles cross-page overlap.
    const combinedSessions = (() => {
      const map = new Map()
      // galleryItems first so its entries win on conflict (most-recently-
      // patched local state lives there for the active flow).
      for (const g of galleryItems) map.set(g.sessionId, g)
      for (const g of reviewedItems) {
        if (!map.has(g.sessionId)) map.set(g.sessionId, g)
      }
      return Array.from(map.values())
    })()
    const allTiles = buildDisplayedTiles(combinedSessions)
    const allActiveTiles = allTiles.filter(tile => {
      if (tile.kind === 'photo') return !tile.g.reviewedAt
      // Group tile: keep in Active unless EVERY session is reviewed.
      return tile.sessions.some(s => !s.reviewedAt)
    })

    const totalRendered = (activeBatch?.pending.length ?? 0)
      + (activeBatch?.failed.length ?? 0)
      + allActiveTiles.length
    const hasGalleryItems = galleryItems.length > 0
    // Used by both the active grid and the reviewed section. Renamed to
    // avoid masking the local just above; both code paths read this.
    const displayedTiles = allActiveTiles

    return (
      <div>
        {hasGalleryItems && (
          <div style={{
            display: 'flex', justifyContent: 'flex-end',
            marginBottom: 8,
          }}>
            <button
              onClick={toggleSelectMode}
              className="os-sandbox-readlabel"
              style={{
                background: selectMode ? C.amber : C.bg,
                border: `1px solid ${selectMode ? C.amber : C.border}`,
                color: selectMode ? '#fff' : C.amber,
                borderRadius: 12,
                padding: '4px 12px',
                fontFamily: fs, fontSize: 12, fontWeight: 600,
                cursor: 'pointer',
                display: 'inline-flex', alignItems: 'center', gap: 6,
              }}
            >
              {selectMode
                ? (<><CheckSquare size={12} strokeWidth={2.4} /> Done selecting</>)
                : (<><CheckSquare size={12} strokeWidth={2.4} /> Select</>)
              }
            </button>
          </div>
        )}

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
          gap: 8,
        }}>
          {/* Pending in-flight items */}
          {activeBatch?.pending.map(p => (
            <div
              key={p.fileId}
              style={{
                position: 'relative', aspectRatio: '1 / 1',
                borderRadius: 8, overflow: 'hidden',
                border: `2px solid ${C.border}`,
                background: '#000',
                cursor: 'wait',
              }}
            >
              <img src={p.blobUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
              <div style={{
                position: 'absolute', inset: 0,
                background: 'rgba(0,0,0,0.55)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <Loader size={22} color="#fff" strokeWidth={2} style={{ animation: 'ownersandbox-spin 1s linear infinite' }} />
              </div>
            </div>
          ))}

          {/* Failed items */}
          {activeBatch?.failed.map(failed => (
            <div
              key={failed.fileId}
              onClick={() => handleRetry(failed.fileId)}
              style={{
                position: 'relative', aspectRatio: '1 / 1',
                borderRadius: 8, overflow: 'hidden',
                border: `2px solid ${C.danger}`,
                background: '#000',
                cursor: 'pointer',
              }}
            >
              <img src={failed.blobUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
              <div style={{
                position: 'absolute', inset: 0,
                background: 'rgba(176,48,32,0.78)',
                display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center',
                color: '#fff', textAlign: 'center', padding: 6,
              }}>
                <div style={{ fontSize: 11, fontFamily: fs, fontWeight: 700, marginBottom: 2 }}>Failed</div>
                <div style={{ fontSize: 10, fontFamily: fs }}>Tap to retry</div>
              </div>
              <button
                onClick={e => { e.stopPropagation(); handleDismissFailed(failed.fileId) }}
                aria-label="Dismiss"
                style={{
                  position: 'absolute', top: 4, right: 4,
                  width: 18, height: 18, borderRadius: '50%',
                  background: '#fff', border: 'none', cursor: 'pointer',
                  padding: 0, lineHeight: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >
                <X size={10} color={C.danger} />
              </button>
            </div>
          ))}

          {/* Persisted gallery — one tile per ungrouped session, one tile
              per sandbox_object (collapsed). Sorted by max(created_at). */}
          {displayedTiles.map(tile => {
            if (tile.kind === 'photo') {
              const g = tile.g
              const items = g.itemsDetected ?? 0
              const isFailed = g.status === 'failed'
              const hasCorrections = (g.correctionsCount ?? 0) > 0
              const isEmpty = items === 0
              const isSelected = selectMode && selectedSessionIds.has(g.sessionId)
              const borderColor = isSelected ? C.amber
                : isFailed ? C.danger
                : hasCorrections ? C.success
                : isEmpty ? C.border
                : C.amber
              const sourceLabel = g.batchSource || g.sandboxSource || '—'
              const topLabel = topKeptDetectionLabel(g)
              const primaryCaption = topLabel || sourceLabel
              return (
                <div
                  key={`p-${g.sessionId}`}
                  onClick={() => {
                    if (selectMode) toggleSessionSelection(g.sessionId)
                    else openDrillIn(g.sessionId)
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  <div style={{
                    position: 'relative', aspectRatio: '1 / 1',
                    borderRadius: 8, overflow: 'hidden',
                    border: `${isSelected ? '3px' : '2px'} solid ${borderColor}`,
                    background: '#000',
                    opacity: isEmpty && !isSelected ? 0.7 : 1,
                    boxShadow: isSelected ? '0 0 0 2px rgba(184,114,31,0.35)' : 'none',
                    transition: 'box-shadow 120ms ease-out, border-color 120ms ease-out',
                  }}>
                    {g.photoUrl ? (
                      <img src={g.photoUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                    ) : (
                      <div style={{ width: '100%', height: '100%', background: C.bg }} />
                    )}
                    {selectMode ? (
                      <div
                        aria-label={isSelected ? 'Selected' : 'Not selected'}
                        style={{
                          position: 'absolute', top: 4, left: 4,
                          width: 24, height: 24, borderRadius: 6,
                          background: isSelected ? C.amber : 'rgba(0,0,0,0.55)',
                          border: `1px solid ${isSelected ? C.amber : 'rgba(255,255,255,0.4)'}`,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          pointerEvents: 'none',
                        }}
                      >
                        {isSelected
                          ? <Check size={14} color="#fff" strokeWidth={3} />
                          : <Square size={14} color="rgba(255,255,255,0.7)" strokeWidth={2} />
                        }
                      </div>
                    ) : (
                      <button
                        onClick={e => {
                          e.stopPropagation()
                          setConfirmDeleteSession({
                            sessionId: g.sessionId,
                            photoUrl: g.photoUrl,
                            batchId: g.batchId,
                          })
                        }}
                        aria-label="Delete photo"
                        style={{
                          position: 'absolute', top: 4, left: 4,
                          width: 22, height: 22, borderRadius: '50%',
                          background: 'rgba(0,0,0,0.55)',
                          border: '1px solid rgba(255,255,255,0.25)',
                          padding: 0, lineHeight: 0, cursor: 'pointer',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}
                      >
                        <X size={12} color="#fff" strokeWidth={2.4} />
                      </button>
                    )}
                    <div style={{
                      position: 'absolute', top: 4, right: 4,
                      background: isEmpty ? 'rgba(154,123,104,0.92)' : 'rgba(184,114,31,0.92)',
                      color: '#fff',
                      padding: '2px 7px', borderRadius: 999,
                      fontSize: 11, fontFamily: fs, fontWeight: 700,
                      lineHeight: 1.3,
                    }}>
                      {isEmpty ? 'no items' : `${items} item${items === 1 ? '' : 's'}`}
                    </div>
                    {hasCorrections && (
                      <div style={{
                        position: 'absolute', bottom: 4, left: 4,
                        background: C.success, color: '#fff',
                        width: 18, height: 18, borderRadius: '50%',
                        fontSize: 10, fontFamily: fs, fontWeight: 700,
                        lineHeight: '18px', textAlign: 'center',
                      }}>
                        {g.correctionsCount}
                      </div>
                    )}
                  </div>
                  <div style={{
                    fontSize: 11, color: C.muted, fontFamily: fs,
                    padding: '4px 2px 0',
                    display: 'flex', justifyContent: 'space-between', gap: 4,
                    overflow: 'hidden',
                  }}>
                    <span style={{
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      color: topLabel ? C.brown : C.muted,
                      fontWeight: topLabel ? 600 : 400,
                    }}>{primaryCaption}</span>
                    <span style={{ flexShrink: 0 }}>{shortDate(g.createdAt)}</span>
                  </div>
                </div>
              )
            }

            // tile.kind === 'group' — collapsed render
            const rep = tile.representative
            const sessionIds = tile.sessions.map(s => s.sessionId)
            const allSelected = selectMode && sessionIds.every(id => selectedSessionIds.has(id))
            const siblings = tile.sessions.slice(1) // exclude representative
            const visibleSiblings = siblings.slice(0, 3)
            const overflowCount = tile.sessions.length > 4 ? tile.sessions.length - 4 : 0
            const fallbackSourceLabel = rep.batchSource || rep.sandboxSource || '—'
            const primaryCaption = tile.canonicalLabel || fallbackSourceLabel
            const isLabelFromCanonical = !!tile.canonicalLabel
            // Sum of active corrections across every loaded session in the
            // group. Per-session counts already include both detection-
            // level and photo-level corrections (loadGalleryPage queries
            // ai_corrections by session_id), so a simple sum is accurate.
            const groupCorrectionsCount = tile.sessions.reduce(
              (sum, s) => sum + (s.correctionsCount ?? 0), 0,
            )
            return (
              <div
                key={`g-${tile.objectId}`}
                onClick={() => {
                  if (selectMode) toggleGroupedTileSelection(sessionIds)
                  else openGroupDetail(tile.objectId)
                }}
                style={{ cursor: 'pointer' }}
              >
                <div style={{
                  position: 'relative', aspectRatio: '1 / 1',
                  borderRadius: 8, overflow: 'hidden',
                  border: `${allSelected ? '3px' : '2px'} solid ${allSelected ? C.amber : C.success}`,
                  background: '#000',
                  boxShadow: allSelected ? '0 0 0 2px rgba(184,114,31,0.35)' : 'none',
                  transition: 'box-shadow 120ms ease-out, border-color 120ms ease-out',
                }}>
                  {rep.photoUrl ? (
                    <img src={rep.photoUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                  ) : (
                    <div style={{ width: '100%', height: '100%', background: C.bg }} />
                  )}
                  {selectMode && (
                    <div
                      aria-label={allSelected ? 'Selected' : 'Not selected'}
                      style={{
                        position: 'absolute', top: 4, left: 4,
                        width: 24, height: 24, borderRadius: 6,
                        background: allSelected ? C.amber : 'rgba(0,0,0,0.55)',
                        border: `1px solid ${allSelected ? C.amber : 'rgba(255,255,255,0.4)'}`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        pointerEvents: 'none',
                      }}
                    >
                      {allSelected
                        ? <Check size={14} color="#fff" strokeWidth={3} />
                        : <Square size={14} color="rgba(255,255,255,0.7)" strokeWidth={2} />
                      }
                    </div>
                  )}
                  {/* Group badge — top-right, replaces the per-photo
                      detection count (which is per-session and not
                      meaningful at the group level). */}
                  <div
                    title="Grouped object"
                    style={{
                      position: 'absolute', top: 4, right: 4,
                      background: 'rgba(46,107,74,0.92)', color: '#fff',
                      padding: '2px 7px', borderRadius: 999,
                      fontSize: 11, fontFamily: fs, fontWeight: 700,
                      lineHeight: 1.3,
                      display: 'inline-flex', alignItems: 'center', gap: 4,
                      boxShadow: '0 1px 3px rgba(0,0,0,0.35)',
                    }}
                  >
                    <Layers size={11} color="#fff" strokeWidth={2.4} />
                    {tile.sessions.length}
                  </div>
                  {/* Group corrections sum badge — bottom-left, same
                      placement as the per-photo corrections badge. */}
                  {groupCorrectionsCount > 0 && (
                    <div style={{
                      position: 'absolute', bottom: 4, left: 4,
                      background: C.success, color: '#fff',
                      minWidth: 18, height: 18, borderRadius: 999, padding: '0 5px',
                      fontSize: 10, fontFamily: fs, fontWeight: 700,
                      lineHeight: '18px', textAlign: 'center',
                      boxShadow: '0 1px 2px rgba(0,0,0,0.25)',
                    }}>
                      {groupCorrectionsCount}
                    </div>
                  )}
                  {/* Sibling preview strip — bottom-right, decorative. Not
                      clickable to keep the tile a single click target. */}
                  {!selectMode && visibleSiblings.length > 0 && (
                    <div style={{
                      position: 'absolute', bottom: 4, right: 4,
                      display: 'flex', alignItems: 'center', gap: 3,
                      pointerEvents: 'none',
                    }}>
                      {visibleSiblings.map((s, i) => (
                        s.photoUrl ? (
                          <img
                            key={s.sessionId}
                            src={s.photoUrl}
                            alt=""
                            style={{
                              width: 24, height: 24, objectFit: 'cover',
                              borderRadius: 4,
                              border: '1.5px solid rgba(255,255,255,0.9)',
                              boxShadow: '0 1px 2px rgba(0,0,0,0.3)',
                              background: '#000',
                            }}
                          />
                        ) : null
                      ))}
                      {overflowCount > 0 && (
                        <div style={{
                          minWidth: 24, height: 24,
                          padding: '0 5px',
                          borderRadius: 4,
                          border: '1.5px solid rgba(255,255,255,0.9)',
                          background: 'rgba(0,0,0,0.7)',
                          color: '#fff',
                          fontFamily: fs, fontSize: 11, fontWeight: 700,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          boxShadow: '0 1px 2px rgba(0,0,0,0.3)',
                        }}>
                          +{overflowCount}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <div style={{
                  fontSize: 11, color: C.muted, fontFamily: fs,
                  padding: '4px 2px 0',
                  display: 'flex', justifyContent: 'space-between', gap: 4,
                  overflow: 'hidden',
                }}>
                  <span style={{
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    color: isLabelFromCanonical ? C.brown : C.muted,
                    fontWeight: isLabelFromCanonical ? 600 : 400,
                  }}>{primaryCaption}</span>
                  <span style={{ flexShrink: 0 }}>{tile.sessions.length} photo{tile.sessions.length === 1 ? '' : 's'}</span>
                </div>
              </div>
            )
          })}
        </div>

        {/* Empty state */}
        {!galleryInitialLoading && totalRendered === 0 && (
          <div style={{
            background: C.card, border: `1px solid ${C.border}`, borderRadius: 12,
            padding: '40px 20px', textAlign: 'center',
            fontFamily: fs, color: C.muted, fontSize: 13, lineHeight: 1.6,
          }}>
            No sandbox photos yet. Pick a source, drop some photos in the
            upload area above, and analyze.
          </div>
        )}

        {/* Loading + sentinel */}
        {galleryInitialLoading && (
          <div style={{ textAlign: 'center', padding: 30 }}>
            <Loader size={32} color={C.amber} strokeWidth={1.5} style={{ animation: 'ownersandbox-spin 1s linear infinite' }} />
          </div>
        )}
        {!galleryInitialLoading && galleryHasMore && (
          <div ref={sentinelRef} style={{ height: 24, textAlign: 'center', padding: 10 }}>
            {galleryLoading && (
              <Loader size={20} color={C.muted} strokeWidth={1.5} style={{ animation: 'ownersandbox-spin 1s linear infinite' }} />
            )}
          </div>
        )}
        {!galleryInitialLoading && !galleryHasMore && galleryItems.length > 0 && (
          <div style={{ textAlign: 'center', padding: '14px 0', color: C.muted, fontSize: 11, fontFamily: fs }}>
            End of gallery · {displayedTiles.length} tile{displayedTiles.length === 1 ? '' : 's'} ({galleryItems.length} photo{galleryItems.length === 1 ? '' : 's'})
          </div>
        )}

        {/* Reviewed section — collapsed by default. Tiles built from a
            separate query (loaded on first expand). Cross-references
            galleryItems to suppress groups that have unreviewed siblings
            in Active (those stay in Active). */}
        {renderReviewedSection()}
      </div>
    )
  }

  // ----- Render: reviewed section --------------------------------------

  function renderReviewedSection() {
    // Same union as renderGallery — gives us a complete view of each
    // group's sessions so the "every session reviewed" check is correct
    // (Bugs 2 + 3). A group with any unreviewed session falls out here.
    const combinedSessions = (() => {
      const map = new Map()
      for (const g of galleryItems) map.set(g.sessionId, g)
      for (const g of reviewedItems) {
        if (!map.has(g.sessionId)) map.set(g.sessionId, g)
      }
      return Array.from(map.values())
    })()
    const reviewedTilesFiltered = buildDisplayedTiles(combinedSessions).filter(tile => {
      if (tile.kind === 'photo') return !!tile.g.reviewedAt
      return tile.sessions.length > 0 && tile.sessions.every(s => s.reviewedAt)
    })
    const count = reviewedTilesFiltered.length
    return (
      <div style={{ marginTop: 18 }}>
        <div
          onClick={toggleReviewedExpanded}
          style={{
            cursor: 'pointer', userSelect: 'none',
            background: C.card, border: `1px solid ${C.border}`, borderRadius: 12,
            padding: '12px 16px',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            marginBottom: reviewedExpanded ? 10 : 0,
          }}
        >
          <h3 style={{ fontFamily: f, fontSize: 14, color: C.muted, margin: 0 }}>
            {reviewedExpanded ? '▾' : '▸'} Reviewed
            {reviewedExpanded && reviewedLoading ? ' · loading…' : (count > 0 ? ` (${count})` : '')}
            {reviewedExpanded && !reviewedLoading && count > 0 && (
              <span style={{ fontSize: 11, fontFamily: fs, fontWeight: 400, color: C.muted, marginLeft: 8 }}>
                — most recent first
              </span>
            )}
          </h3>
          {reviewedHitCap && (
            <span style={{ fontSize: 11, color: C.muted, fontFamily: fs }}>
              Showing 100 most recent
            </span>
          )}
        </div>
        {reviewedExpanded && !reviewedLoading && count === 0 && (
          <div style={{
            background: C.card, border: `1px solid ${C.border}`, borderRadius: 12,
            padding: '20px', textAlign: 'center',
            fontFamily: fs, color: C.muted, fontSize: 13, fontStyle: 'italic',
          }}>
            Nothing reviewed yet. Mark a photo or group reviewed from inside its drill-in or detail view.
          </div>
        )}
        {reviewedExpanded && count > 0 && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
            gap: 8,
          }}>
            {reviewedTilesFiltered.map(tile => {
              // Reviewed footer: small "Reviewed N ago" under the existing
              // caption. Wrap the tile render and append the footer.
              const reviewedAt = tile.kind === 'photo'
                ? tile.g.reviewedAt
                : (tile.sessions.find(s => s.reviewedAt)?.reviewedAt || null)
              return (
                <div key={`r-${tile.kind === 'photo' ? tile.g.sessionId : tile.objectId}`}>
                  {renderReviewedTile(tile)}
                  {reviewedAt && (
                    <div style={{ fontSize: 10, color: C.muted, fontFamily: fs, padding: '2px 2px 0', fontStyle: 'italic' }}>
                      Reviewed {relTime(reviewedAt)}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </div>
    )
  }

  // Minimal tile for the Reviewed section. Same visual treatment as the
  // active tile but: no × delete, no select-mode checkbox, no sibling
  // strip (decorative — group tiles still show the count badge). Click
  // routes through the same drill-in / openGroupDetail handlers.
  function renderReviewedTile(tile) {
    if (tile.kind === 'photo') {
      const g = tile.g
      const items = g.itemsDetected ?? 0
      const hasCorrections = (g.correctionsCount ?? 0) > 0
      const isEmpty = items === 0
      const sourceLabel = g.batchSource || g.sandboxSource || '—'
      const topLabel = topKeptDetectionLabel(g)
      const primaryCaption = topLabel || sourceLabel
      return (
        <div
          onClick={() => openDrillIn(g.sessionId)}
          style={{ cursor: 'pointer' }}
        >
          <div style={{
            position: 'relative', aspectRatio: '1 / 1',
            borderRadius: 8, overflow: 'hidden',
            border: `2px solid ${C.border}`,
            background: '#000',
            opacity: 0.85,
          }}>
            {g.photoUrl
              ? <img src={g.photoUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
              : <div style={{ width: '100%', height: '100%', background: C.bg }} />}
            <div style={{
              position: 'absolute', top: 4, right: 4,
              background: isEmpty ? 'rgba(154,123,104,0.92)' : 'rgba(184,114,31,0.92)',
              color: '#fff',
              padding: '2px 7px', borderRadius: 999,
              fontSize: 11, fontFamily: fs, fontWeight: 700, lineHeight: 1.3,
            }}>
              {isEmpty ? 'no items' : `${items} item${items === 1 ? '' : 's'}`}
            </div>
            {hasCorrections && (
              <div style={{
                position: 'absolute', bottom: 4, left: 4,
                background: C.success, color: '#fff',
                width: 18, height: 18, borderRadius: '50%',
                fontSize: 10, fontFamily: fs, fontWeight: 700,
                lineHeight: '18px', textAlign: 'center',
              }}>
                {g.correctionsCount}
              </div>
            )}
          </div>
          <div style={{
            fontSize: 11, color: C.muted, fontFamily: fs,
            padding: '4px 2px 0',
            display: 'flex', justifyContent: 'space-between', gap: 4,
            overflow: 'hidden',
          }}>
            <span style={{
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              color: topLabel ? C.brown : C.muted,
              fontWeight: topLabel ? 600 : 400,
            }}>{primaryCaption}</span>
            <span style={{ flexShrink: 0 }}>{shortDate(g.createdAt)}</span>
          </div>
        </div>
      )
    }
    // Group tile (reviewed section)
    const rep = tile.representative
    const fallbackSource = rep.batchSource || rep.sandboxSource || '—'
    const primaryCaption = tile.canonicalLabel || fallbackSource
    const groupCorrectionsCount = tile.sessions.reduce(
      (sum, s) => sum + (s.correctionsCount ?? 0), 0,
    )
    return (
      <div
        onClick={() => openGroupDetail(tile.objectId)}
        style={{ cursor: 'pointer' }}
      >
        <div style={{
          position: 'relative', aspectRatio: '1 / 1',
          borderRadius: 8, overflow: 'hidden',
          border: `2px solid ${C.border}`,
          background: '#000',
          opacity: 0.85,
        }}>
          {rep.photoUrl
            ? <img src={rep.photoUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
            : <div style={{ width: '100%', height: '100%', background: C.bg }} />}
          <div style={{
            position: 'absolute', top: 4, right: 4,
            background: 'rgba(46,107,74,0.92)', color: '#fff',
            padding: '2px 7px', borderRadius: 999,
            fontSize: 11, fontFamily: fs, fontWeight: 700, lineHeight: 1.3,
            display: 'inline-flex', alignItems: 'center', gap: 4,
          }}>
            <Layers size={11} color="#fff" strokeWidth={2.4} />
            {tile.sessions.length}
          </div>
          {groupCorrectionsCount > 0 && (
            <div style={{
              position: 'absolute', bottom: 4, left: 4,
              background: C.success, color: '#fff',
              minWidth: 18, height: 18, borderRadius: 999, padding: '0 5px',
              fontSize: 10, fontFamily: fs, fontWeight: 700,
              lineHeight: '18px', textAlign: 'center',
              boxShadow: '0 1px 2px rgba(0,0,0,0.25)',
            }}>
              {groupCorrectionsCount}
            </div>
          )}
        </div>
        <div style={{
          fontSize: 11, color: C.muted, fontFamily: fs,
          padding: '4px 2px 0',
          display: 'flex', justifyContent: 'space-between', gap: 4,
          overflow: 'hidden',
        }}>
          <span style={{
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            color: tile.canonicalLabel ? C.brown : C.muted,
            fontWeight: tile.canonicalLabel ? 600 : 400,
          }}>{primaryCaption}</span>
          <span style={{ flexShrink: 0 }}>{tile.sessions.length} photo{tile.sessions.length === 1 ? '' : 's'}</span>
        </div>
      </div>
    )
  }

  // ----- Render: recent corrections ------------------------------------

  function renderRecentCorrections() {
    return (
      <div style={cardStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 10 }}>
          <h3 style={{ fontFamily: f, fontSize: 15, color: C.brown, margin: 0 }}>Recent corrections</h3>
          {recentCorrections.length > 0 && (
            <span style={{ fontSize: 11, color: C.muted }}>last {recentCorrections.length}</span>
          )}
        </div>
        {recentCorrections.length === 0 && (
          <div style={{ fontSize: 13, color: C.muted, fontStyle: 'italic' }}>
            No corrections yet. Tap a thumbnail and save your first one.
          </div>
        )}
        {recentCorrections.map(c => {
          const thumbUrl = c.room_scan_sessions?.source_photo_urls?.[0] || null
          const groupId = c.room_scan_sessions?.sandbox_object_id || null
          const canNavigate = !!(groupId || c.session_id)
          const goToCorrection = () => {
            if (!canNavigate) return
            if (groupId) openGroupDetail(groupId)
            else openDrillIn(c.session_id)
          }
          return (
            <div
              key={c.id}
              onClick={goToCorrection}
              className="os-sandbox-recent-row"
              style={{
                padding: '8px 6px',
                borderBottom: `1px solid ${C.border}`,
                display: 'flex', alignItems: 'flex-start', gap: 10,
                cursor: canNavigate ? 'pointer' : 'default',
                borderRadius: 6,
                transition: 'background 120ms ease-out',
              }}
            >
              {thumbUrl ? (
                <img
                  src={thumbUrl}
                  alt=""
                  style={{
                    flexShrink: 0, width: 32, height: 32, borderRadius: 4,
                    objectFit: 'cover', background: '#000',
                    border: `1px solid ${C.border}`,
                  }}
                />
              ) : (
                <div
                  aria-label="No thumbnail available"
                  style={{
                    flexShrink: 0, width: 32, height: 32, borderRadius: 4,
                    background: C.bg, border: `1px solid ${C.border}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: C.muted,
                  }}
                >
                  <ImageIcon size={14} strokeWidth={1.6} />
                </div>
              )}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8, fontFamily: fs, fontSize: 13 }}>
                  <div style={{ color: C.brown, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    <span style={{ fontWeight: 700 }}>{c.correction_type}</span>
                    {c.ai_value || c.user_value ? (
                      <>
                        {': '}
                        {c.ai_value ? <span style={{ color: C.muted }}>"{c.ai_value}"</span> : <em style={{ color: C.muted }}>(empty)</em>}
                        {c.user_value && <span> → <strong>"{c.user_value}"</strong></span>}
                      </>
                    ) : null}
                  </div>
                  <div style={{ color: C.muted, fontSize: 11, whiteSpace: 'nowrap', flexShrink: 0 }}>
                    {c.context_source ? `${c.context_source} · ` : ''}{relTime(c.created_at)}
                  </div>
                </div>
                {c.notes && (
                  <div style={{ fontSize: 12, color: C.muted, marginTop: 3, fontStyle: 'italic' }}>{c.notes}</div>
                )}
              </div>
            </div>
          )
        })}
      </div>
    )
  }

  // ----- Render: analytics dashboard -----------------------------------

  function renderAnalytics() {
    if (analyticsLoading && !analyticsData) {
      return (
        <div style={{ ...cardStyle, textAlign: 'center', padding: 30 }}>
          <Loader size={28} color={C.amber} strokeWidth={1.5} style={{ animation: 'ownersandbox-spin 1s linear infinite' }} />
        </div>
      )
    }
    if (!analyticsData) {
      return (
        <div style={{ ...cardStyle, color: C.muted, fontStyle: 'italic', fontSize: 13 }}>
          No analytics yet.
        </div>
      )
    }
    const a = analyticsData
    const maxTypeCount = a.byType.reduce((m, r) => Math.max(m, r.count), 0)
    const maxWeekCount = a.byWeek.reduce((m, r) => Math.max(m, r.count), 0)

    const headlineNumStyle = { fontFamily: f, fontSize: 28, color: C.brown, fontWeight: 700, lineHeight: 1.1 }
    const headlineLabelStyle = { fontFamily: fs, fontSize: 12, color: C.muted, marginTop: 4 }

    return (
      <>
        {/* SECTION 1 — Headline numbers */}
        <div style={{ ...cardStyle, display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
          <div>
            <div style={headlineNumStyle}>{a.photosAnalyzed}</div>
            <div style={headlineLabelStyle}>Photos analyzed</div>
          </div>
          <div>
            <div style={headlineNumStyle}>{a.detectionsCount}</div>
            <div style={headlineLabelStyle}>Detections</div>
          </div>
          <div>
            <div style={headlineNumStyle}>{a.activeCorrections}</div>
            <div style={headlineLabelStyle}>Active corrections</div>
          </div>
        </div>

        {/* SECTION 2 — Corrections by type */}
        <div style={cardStyle}>
          <h3 style={{ fontFamily: f, fontSize: 15, color: C.brown, margin: '0 0 12px' }}>
            What you've corrected
          </h3>
          {a.byType.length === 0 ? (
            <div style={{ fontSize: 13, color: C.muted, fontStyle: 'italic' }}>No data yet</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {a.byType.map(row => {
                const pct = maxTypeCount > 0 ? (row.count / maxTypeCount) * 100 : 0
                return (
                  <div key={row.type} style={{ display: 'flex', alignItems: 'center', gap: 10, fontFamily: fs, fontSize: 13 }}>
                    <div style={{ width: 220, color: C.brown, flexShrink: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {CORRECTION_TYPE_DISPLAY[row.type] || row.type}
                    </div>
                    <div style={{ flex: 1, background: C.bg, borderRadius: 4, height: 16, overflow: 'hidden' }}>
                      <div style={{
                        width: `${pct}%`,
                        height: '100%',
                        background: C.amber,
                        transition: 'width 240ms ease-out',
                      }} />
                    </div>
                    <div style={{ width: 36, textAlign: 'right', color: C.brown, fontWeight: 600 }}>{row.count}</div>
                  </div>
                )
              })}
            </div>
          )}
        </div>

        {/* SECTION 3 — Corrections over time */}
        <div style={cardStyle}>
          <h3 style={{ fontFamily: f, fontSize: 15, color: C.brown, margin: '0 0 12px' }}>
            Corrections logged by week
          </h3>
          {a.byWeek.length === 0 ? (
            <div style={{ fontSize: 13, color: C.muted, fontStyle: 'italic' }}>No data yet</div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, height: 110, paddingTop: 4 }}>
              {a.byWeek.map(row => {
                const h = maxWeekCount > 0 ? Math.max(4, (row.count / maxWeekCount) * 80) : 4
                return (
                  <div key={row.week} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, minWidth: 40 }}>
                    <div style={{ fontSize: 10, color: C.muted, fontFamily: fs, height: 14 }}>{row.count}</div>
                    <div style={{
                      width: 28, height: h,
                      background: C.amber, borderRadius: 4,
                      transition: 'height 240ms ease-out',
                    }} />
                    <div style={{ fontSize: 10, color: C.muted, fontFamily: fs, whiteSpace: 'nowrap' }}>
                      {shortWeekLabel(row.week)}
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>

        {/* SECTION 4 — Calibration sources */}
        <div style={cardStyle}>
          <h3 style={{ fontFamily: f, fontSize: 15, color: C.brown, margin: '0 0 12px' }}>
            Calibration sources
          </h3>
          {a.bySource.length === 0 ? (
            <div style={{ fontSize: 13, color: C.muted, fontStyle: 'italic', lineHeight: 1.5 }}>
              No source-tagged corrections yet. Tag photos with a source (Open House, Hardware Store, etc.) when uploading to track which channels generate the most calibration data.
            </div>
          ) : (
            <div>
              <div style={{
                display: 'grid', gridTemplateColumns: '1fr auto auto', gap: '6px 16px',
                fontSize: 11, color: C.muted, fontFamily: fs, fontWeight: 700,
                letterSpacing: 1.2, textTransform: 'uppercase',
                paddingBottom: 6, borderBottom: `1px solid ${C.border}`,
              }}>
                <div>Source</div>
                <div style={{ textAlign: 'right' }}>Corrections</div>
                <div style={{ textAlign: 'right' }}>Photos</div>
              </div>
              {a.bySource.map(row => (
                <div key={row.source} style={{
                  display: 'grid', gridTemplateColumns: '1fr auto auto', gap: '6px 16px',
                  padding: '8px 0', borderBottom: `1px solid ${C.border}`,
                  fontFamily: fs, fontSize: 13, color: C.brown,
                }}>
                  <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {row.source}
                  </div>
                  <div style={{ textAlign: 'right', fontWeight: 600 }}>{row.correction_count}</div>
                  <div style={{ textAlign: 'right', color: C.muted }}>{row.photo_count}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* SECTION 5 — Deep reads */}
        <div style={cardStyle}>
          <h3 style={{ fontFamily: f, fontSize: 15, color: C.brown, margin: '0 0 12px' }}>
            Deep reads
          </h3>
          <div style={headlineNumStyle}>{a.deepReads}</div>
          <div style={{ fontFamily: fs, fontSize: 13, color: C.muted, marginTop: 6 }}>
            {a.deepReads === 1
              ? 'detection has been deep-read for additional data plate fields.'
              : 'detections have been deep-read for additional data plate fields.'}
          </div>
        </div>
      </>
    )
  }

  // ----- Render: grouped object detail ---------------------------------

  function renderGroupedObject() {
    if (viewingGroupLoading && !viewingGroupData) {
      return (
        <div style={{ ...cardStyle, textAlign: 'center', padding: 30 }}>
          <Loader size={28} color={C.amber} strokeWidth={1.5} style={{ animation: 'ownersandbox-spin 1s linear infinite' }} />
        </div>
      )
    }
    if (!viewingGroupData) return null
    const { object, sessions } = viewingGroupData
    const fields = [
      ['Make', object.canonical_make],
      ['Model', object.canonical_model],
      ['Serial', object.canonical_serial],
      ['Category', object.category],
      ['Notes', object.notes],
    ].filter(([, v]) => v && String(v).trim().length > 0)

    // Order: primary photo first, then the rest in created_at order.
    // Drag drop is only persisted when something becomes (or stops
    // being) the primary; non-zero-position drops are visual-only,
    // which means non-primary order resets on next page load.
    const primaryId = object.primary_session_id
    const orderedSessions = (() => {
      if (!primaryId) return sessions
      const primary = sessions.find(s => s.id === primaryId)
      if (!primary) return sessions
      return [primary, ...sessions.filter(s => s.id !== primaryId)]
    })()
    const allReviewed = sessions.length > 0 && sessions.every(s => s.reviewed_at)

    return (
      <>
        <div style={cardStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
            <div style={{ minWidth: 0, flex: 1 }}>
              <h2 style={{ fontFamily: f, fontSize: 20, color: C.brown, margin: '0 0 6px' }}>
                {object.canonical_label || 'Untitled object'}
              </h2>
              <div style={{ fontSize: 11, color: C.muted, fontFamily: fs, marginBottom: fields.length ? 14 : 0 }}>
                {sessions.length} photo{sessions.length === 1 ? '' : 's'} · grouped manually
              </div>
            </div>
            <button
              onClick={async () => {
                const newValue = allReviewed ? null : new Date().toISOString()
                await setGroupReviewedAt(object.id, newValue)
                closeGroupDetail()
              }}
              disabled={markingReviewed || sessions.length === 0}
              style={{
                background: allReviewed ? '#fff' : C.success,
                color: allReviewed ? C.brown : '#fff',
                border: `1px solid ${allReviewed ? C.border : C.success}`,
                borderRadius: 10, padding: '8px 14px',
                fontFamily: f, fontSize: 13, fontWeight: 700,
                cursor: markingReviewed ? 'wait' : 'pointer',
                opacity: markingReviewed ? 0.6 : 1,
                whiteSpace: 'nowrap', flexShrink: 0,
              }}
            >
              {allReviewed ? 'Mark all unreviewed' : 'Mark all reviewed'}
            </button>
          </div>
          {fields.length > 0 && (
            <div style={{
              display: 'grid', gridTemplateColumns: '110px 1fr', gap: '6px 14px',
              fontFamily: fs, fontSize: 13,
            }}>
              {fields.flatMap(([label, value]) => [
                <div key={`l-${label}`} style={{ color: C.muted, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, fontSize: 10, alignSelf: 'center' }}>
                  {label}
                </div>,
                <div key={`v-${label}`} style={{ color: C.brown }}>{value}</div>,
              ])}
            </div>
          )}
        </div>

        {/* Group-level corrections — consolidates active corrections
            across all constituent sessions. Each chip carries a thumbnail
            indicator showing which photo (and which detection, when
            relevant) the correction belongs to. Click thumbnail or chip
            body to drill in / open the edit form. */}
        {(viewingGroupData.correctionsLoading || (viewingGroupData.corrections || []).length > 0) && (
          <div style={cardStyle}>
            <h3 style={{ fontFamily: f, fontSize: 15, color: C.brown, margin: '0 0 10px' }}>
              Corrections across this group ({(viewingGroupData.corrections || []).length})
              {viewingGroupData.correctionsLoading && (
                <span style={{ fontSize: 11, fontFamily: fs, color: C.muted, fontWeight: 400, marginLeft: 8 }}>
                  · loading…
                </span>
              )}
            </h3>
            {!viewingGroupData.correctionsLoading && (viewingGroupData.corrections || []).length === 0 && (
              <div style={{ fontSize: 13, color: C.muted, fontStyle: 'italic' }}>
                No corrections yet.
              </div>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {(viewingGroupData.corrections || []).map(c => {
                const sess = sessions.find(s => s.id === c.session_id)
                const detRow = c.detection_id
                  ? viewingGroupData.detectionsByDetectionId?.get(c.detection_id)
                  : null
                const detLabel = detRow?.refined_label || detRow?.detected_label || null
                const indicator = c.detection_id
                  ? (detLabel ? `on detection: "${detLabel}"` : 'on detection: (deleted)')
                  : 'on photo'
                return (
                  <div key={c.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                    {/* Mini thumbnail — clickable, opens that photo's
                        drill-in. Acts as both visual indicator and a
                        navigation shortcut. */}
                    <button
                      onClick={() => openDrillIn(c.session_id)}
                      aria-label="Open this photo"
                      style={{
                        flexShrink: 0, width: 36, height: 36, borderRadius: 6,
                        padding: 0, border: `1px solid ${C.border}`,
                        background: '#000', cursor: 'pointer', overflow: 'hidden',
                      }}
                    >
                      {sess?.source_photo_urls?.[0]
                        ? <img src={sess.source_photo_urls[0]} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                        : <div style={{ width: '100%', height: '100%' }} />}
                    </button>
                    <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 3 }}>
                      <span
                        onClick={() => openGroupChipEdit(c)}
                        title={c.notes ? `Notes: ${c.notes}` : 'Click to edit'}
                        style={{
                          display: 'inline-flex', alignItems: 'center', gap: 4,
                          background: C.bg, border: `1px solid ${C.border}`,
                          color: C.brown, borderRadius: 12,
                          padding: '4px 4px 4px 10px',
                          fontFamily: fs, fontSize: 12, fontWeight: 600,
                          cursor: 'pointer',
                          alignSelf: 'flex-start',
                          maxWidth: '100%',
                        }}
                      >
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 320 }}>
                          {chipLabelExpanded(c)}
                        </span>
                        <button
                          onClick={e => {
                            e.stopPropagation()
                            setConfirmDeleteCorrection({ correctionId: c.id, label: chipLabelExpanded(c) })
                          }}
                          aria-label="Remove correction"
                          className="os-sandbox-chip-x"
                          style={{
                            background: 'transparent', border: 'none', padding: 0,
                            width: 18, height: 18, borderRadius: '50%',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            cursor: 'pointer', color: C.muted, lineHeight: 0,
                          }}
                        >
                          <X size={12} strokeWidth={2.4} />
                        </button>
                      </span>
                      <span
                        onClick={() => openDrillIn(c.session_id)}
                        style={{
                          fontFamily: fs, fontSize: 11, color: C.muted,
                          fontStyle: 'italic', cursor: 'pointer',
                        }}
                      >
                        {indicator}
                      </span>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        )}

        <div style={cardStyle}>
          <h3 style={{ fontFamily: f, fontSize: 15, color: C.brown, margin: '0 0 6px' }}>
            Photos in this group
          </h3>
          {sessions.length > 1 && (
            <div style={{ fontSize: 12, color: C.muted, fontStyle: 'italic', marginBottom: 10 }}>
              Drag a photo to the first position to make it the primary.
            </div>
          )}
          {sessions.length === 0 ? (
            <div style={{ fontSize: 13, color: C.muted, fontStyle: 'italic' }}>
              All photos in this group have been deleted. Ungroup to clean up.
            </div>
          ) : (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
              gap: 8,
            }}>
              {orderedSessions.map((s, idx) => {
                const url = s.source_photo_urls?.[0]
                const sourceLabel = s.sandbox_batches?.source || s.sandbox_source || '—'
                const items = s.items_detected ?? 0
                const isPrimary = idx === 0
                const isDragging = dragSessionId === s.id
                const isDragOver = dragOverIndex === idx && dragSessionId && dragSessionId !== s.id
                return (
                  <div
                    key={s.id}
                    draggable={sessions.length > 1}
                    onDragStart={e => {
                      setDragSessionId(s.id)
                      try { e.dataTransfer.effectAllowed = 'move' } catch (_) { /* noop */ }
                    }}
                    onDragOver={e => {
                      if (!dragSessionId || dragSessionId === s.id) return
                      e.preventDefault()
                      if (dragOverIndex !== idx) setDragOverIndex(idx)
                    }}
                    onDragLeave={() => {
                      if (dragOverIndex === idx) setDragOverIndex(null)
                    }}
                    onDrop={e => {
                      e.preventDefault()
                      const draggedId = dragSessionId
                      setDragSessionId(null)
                      setDragOverIndex(null)
                      if (!draggedId || draggedId === s.id) return
                      // Drop on position 0 → make dragged the primary.
                      // Drop primary onto a non-zero position → clear primary.
                      if (idx === 0) {
                        if (object.primary_session_id !== draggedId) {
                          setGroupPrimarySession(object.id, draggedId)
                        }
                      } else if (object.primary_session_id === draggedId) {
                        setGroupPrimarySession(object.id, null)
                      }
                      // Other drops are visual-only (ephemeral). No DB write.
                    }}
                    onDragEnd={() => { setDragSessionId(null); setDragOverIndex(null) }}
                    onClick={() => openDrillIn(s.id)}
                    style={{
                      cursor: sessions.length > 1 ? 'grab' : 'pointer',
                      opacity: isDragging ? 0.4 : 1,
                      transition: 'opacity 120ms ease-out',
                    }}
                  >
                    <div style={{
                      position: 'relative', aspectRatio: '1 / 1',
                      borderRadius: 8, overflow: 'hidden',
                      border: `2px solid ${isPrimary ? C.amber : (isDragOver ? C.amber : C.border)}`,
                      background: '#000',
                      boxShadow: isDragOver ? `0 0 0 2px ${C.amber}` : 'none',
                    }}>
                      {url ? (
                        <img src={url} alt="" draggable={false} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', pointerEvents: 'none' }} />
                      ) : (
                        <div style={{ width: '100%', height: '100%', background: C.bg }} />
                      )}
                      {isPrimary && (
                        <div style={{
                          position: 'absolute', top: 4, left: 4,
                          background: C.success, color: '#fff',
                          padding: '2px 7px', borderRadius: 999,
                          fontSize: 10, fontFamily: fs, fontWeight: 700,
                          letterSpacing: 0.5, textTransform: 'uppercase',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.35)',
                        }}>
                          Primary
                        </div>
                      )}
                      <div style={{
                        position: 'absolute', top: 4, right: 4,
                        background: 'rgba(184,114,31,0.92)', color: '#fff',
                        padding: '2px 7px', borderRadius: 999,
                        fontSize: 11, fontFamily: fs, fontWeight: 700, lineHeight: 1.3,
                      }}>
                        {items === 0 ? 'no items' : `${items} item${items === 1 ? '' : 's'}`}
                      </div>
                    </div>
                    <div style={{
                      fontSize: 11, color: C.muted, fontFamily: fs,
                      padding: '4px 2px 0',
                      display: 'flex', justifyContent: 'space-between', gap: 4,
                      overflow: 'hidden',
                    }}>
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{sourceLabel}</span>
                      <span style={{ flexShrink: 0 }}>{shortDate(s.created_at)}</span>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
          {/* TODO: keyboard-accessible drag alternative. Owner-only tonight,
              accept the gap. */}
        </div>

        <div style={{ ...cardStyle, display: 'flex', justifyContent: 'flex-end' }}>
          <button
            onClick={() => setConfirmUngroup(true)}
            style={{
              background: '#fff', color: C.danger,
              border: `1px solid ${C.danger}`, borderRadius: 10,
              padding: '10px 18px', fontFamily: f, fontSize: 14, fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            Ungroup
          </button>
        </div>
      </>
    )
  }

  // ----- Render: drill-in modal ----------------------------------------

  function renderDrillIn() {
    const galleryItem = galleryItems.find(g => g.sessionId === drillInSessionId)
    if (!galleryItem) return null
    const photoUrl = galleryItem.photoUrl
    const items = drillInItems

    return (
      <div
        onClick={e => { if (e.target === e.currentTarget) closeDrillIn() }}
        style={{
          position: 'fixed', inset: 0, zIndex: 2000,
          background: 'rgba(44,29,20,0.85)',
          overflowY: 'auto',
        }}
      >
        <div
          onClick={e => e.stopPropagation()}
          style={{ ...wrap, maxWidth: 960, padding: '20px 16px 80px' }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, gap: 10 }}>
            <button onClick={closeDrillIn} style={{ ...linkBtn, color: '#fff' }}>
              <ArrowLeft size={14} /> Back to gallery
            </button>
            <div style={{ fontFamily: fs, fontSize: 12, color: '#fff', opacity: 0.85, flex: 1, textAlign: 'right', display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 10 }}>
              {galleryItem.sandboxObjectId && (
                <button
                  onClick={() => openGroupDetail(galleryItem.sandboxObjectId)}
                  style={{
                    background: 'rgba(46,107,74,0.85)', color: '#fff',
                    border: '1px solid rgba(255,255,255,0.25)', borderRadius: 999,
                    padding: '4px 10px', fontFamily: fs, fontSize: 11, fontWeight: 600,
                    cursor: 'pointer',
                    display: 'inline-flex', alignItems: 'center', gap: 4,
                  }}
                >
                  <Layers size={11} strokeWidth={2.4} /> View group
                </button>
              )}
              {/* Mark reviewed / unreviewed — single session toggle. The
                  drill-in always shows it; label flips based on current
                  reviewedAt state. Closes the drill-in on success so the
                  photo visibly moves to (or returns from) the Reviewed
                  section. */}
              <button
                onClick={async () => {
                  const wasReviewed = !!galleryItem.reviewedAt
                  await setSessionReviewedAt(
                    galleryItem.sessionId,
                    wasReviewed ? null : new Date().toISOString(),
                  )
                  closeDrillIn()
                }}
                disabled={markingReviewed}
                style={{
                  background: galleryItem.reviewedAt ? 'rgba(154,123,104,0.5)' : 'rgba(46,107,74,0.85)',
                  color: '#fff',
                  border: '1px solid rgba(255,255,255,0.25)', borderRadius: 999,
                  padding: '4px 10px', fontFamily: fs, fontSize: 11, fontWeight: 600,
                  cursor: markingReviewed ? 'wait' : 'pointer',
                  opacity: markingReviewed ? 0.6 : 1,
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                }}
              >
                {galleryItem.reviewedAt ? 'Mark unreviewed' : 'Mark reviewed'}
              </button>
              <span>
                {galleryItem.batchSource || galleryItem.sandboxSource || ''}
                {' · '}
                {items.length} detection{items.length === 1 ? '' : 's'}
                {drillInLoading && ' · loading…'}
              </span>
            </div>
            <button
              onClick={closeDrillIn}
              aria-label="Close drill-in"
              style={{
                width: 32, height: 32, borderRadius: '50%',
                background: 'rgba(255,255,255,0.12)',
                border: '1px solid rgba(255,255,255,0.25)',
                cursor: 'pointer', padding: 0, lineHeight: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <X size={16} color="#fff" strokeWidth={2.2} />
            </button>
          </div>

          <div style={cardStyle}>
            <div
              ref={photoViewportRef}
              onClick={handlePhotoTap}
              onPointerDown={handlePhotoPointerDown}
              onPointerMove={handlePhotoPointerMove}
              onPointerUp={handlePhotoPointerUp}
              onPointerCancel={handlePhotoPointerUp}
              onTouchStart={handlePhotoTouchStart}
              onTouchMove={handlePhotoTouchMove}
              onTouchEnd={handlePhotoTouchEnd}
              onTouchCancel={handlePhotoTouchEnd}
              style={{
                position: 'relative',
                width: '100%', maxWidth: '100%',
                background: '#000', borderRadius: 8, overflow: 'hidden',
                cursor: missedMode
                  ? 'crosshair'
                  : (photoZoom.scale === 1 ? 'zoom-in' : 'grab'),
                touchAction: 'none',
                userSelect: 'none',
              }}
            >
              <div
                style={{
                  position: 'relative',
                  width: '100%',
                  transform: `translate(${photoZoom.x}px, ${photoZoom.y}px) scale(${photoZoom.scale})`,
                  transformOrigin: '50% 50%',
                  transition: 'transform 180ms ease-out',
                  willChange: 'transform',
                }}
              >
                {photoUrl && (
                  <img
                    ref={imgRef}
                    src={photoUrl}
                    alt="Sandbox capture"
                    draggable={false}
                    style={{
                      display: 'block', width: '100%',
                      maxHeight: '60vh', objectFit: 'contain',
                      pointerEvents: 'none',
                    }}
                  />
                )}
                {items.map((it, i) => {
                  const bb = it.bounding_box || {}
                  const left = `${(bb.x_min ?? 0) * 100}%`
                  const top = `${(bb.y_min ?? 0) * 100}%`
                  const width = `${((bb.x_max ?? 0) - (bb.x_min ?? 0)) * 100}%`
                  const height = `${((bb.y_max ?? 0) - (bb.y_min ?? 0)) * 100}%`
                  const isActive = activeCorrection?.mode === 'detection' && activeCorrection.itemIndex === i
                  const isFiltered = it?.ai_details?.should_log === false
                  const baseColor = isActive ? C.success : C.amber
                  return (
                    <div
                      key={i}
                      onClick={e => { e.stopPropagation(); startCorrectingDetection(i) }}
                      style={{
                        position: 'absolute', left, top, width, height,
                        border: `2px ${isFiltered ? 'dashed' : 'solid'} ${baseColor}`,
                        background: isActive ? 'rgba(46,107,74,0.18)' : 'rgba(184,114,31,0.12)',
                        opacity: isFiltered && !isActive ? 0.55 : 1,
                        cursor: missedMode ? 'crosshair' : 'pointer',
                        boxSizing: 'border-box',
                      }}
                    >
                      <span style={{
                        position: 'absolute', top: -22, left: 0,
                        background: baseColor,
                        color: '#fff', padding: '2px 6px',
                        fontSize: 11, fontFamily: fs, fontWeight: 600,
                        borderRadius: 4, whiteSpace: 'nowrap',
                        maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis',
                      }}>
                        {isFiltered && '◌ '}{it.refined_label || it.label || `Item ${i + 1}`}
                      </span>
                    </div>
                  )
                })}
                {activeCorrection?.mode === 'missed' && activeCorrection.missedPin && (
                  <div style={{
                    position: 'absolute',
                    left: `calc(${activeCorrection.missedPin.x * 100}% - 12px)`,
                    top: `calc(${activeCorrection.missedPin.y * 100}% - 12px)`,
                    width: 24, height: 24, borderRadius: '50%',
                    background: C.success, color: '#fff',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    border: '2px solid #fff', boxShadow: '0 1px 4px rgba(0,0,0,0.4)',
                    pointerEvents: 'none',
                  }}>
                    <Plus size={14} strokeWidth={3} />
                  </div>
                )}
              </div>
            </div>

            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
              <button
                onClick={() => { setMissedMode(m => !m); setActiveCorrection(null) }}
                style={{
                  ...secondaryBtn,
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  background: missedMode ? C.successBg : '#fff',
                  color: missedMode ? C.success : C.brown,
                  borderColor: missedMode ? C.success : C.border,
                }}
              >
                <Plus size={14} /> {missedMode ? 'Tap the photo to mark the missed item' : 'Casavio missed something'}
              </button>
              <button onClick={closeDrillIn} style={secondaryBtn}>Back to gallery</button>
            </div>
          </div>

          {activeCorrection && (
            <div style={cardStyle}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 12 }}>
                <h3 style={{ fontFamily: f, fontSize: 16, color: C.brown, margin: 0 }}>
                  {activeCorrection.mode === 'missed'
                    ? 'Mark a missed item'
                    : `${editingCorrectionId ? 'Edit' : 'Correct'}: ${activeCorrection.item?.refined_label || activeCorrection.item?.label || 'Detection'}`}
                </h3>
                <button
                  onClick={() => { setActiveCorrection(null); setEditingCorrectionId(null) }}
                  aria-label="Close"
                  style={{ background: 'transparent', border: 'none', color: C.muted, cursor: 'pointer', fontSize: 18, padding: 0, lineHeight: 1 }}
                >
                  ×
                </button>
              </div>

              {activeCorrection.mode === 'detection' && (
                <div style={{ marginBottom: 12 }}>
                  <label style={labelStyle}>What's wrong?</label>
                  <select
                    value={correctionType}
                    onChange={e => { setCorrectionType(e.target.value); setCorrectionValue('') }}
                    className="os-sandbox-select"
                    style={selectStyle}
                  >
                    <option value="" disabled>Choose what's wrong…</option>
                    {CORRECTION_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
              )}

              {(() => {
                const isMissed = activeCorrection.mode === 'missed'
                // No type selected yet — show only the dropdown + notes.
                // Avoids a stale empty text input flashing before the user picks.
                if (!isMissed && correctionType === '') return null
                const typeDef = CORRECTION_TYPES.find(t => t.value === correctionType)
                const inputKind = isMissed ? 'text' : (typeDef?.inputKind ?? 'text')
                const aiLabel = activeCorrection.item?.refined_label || activeCorrection.item?.label || 'Item'
                const aiConfPct = Math.round(((activeCorrection.item?.confidence_score ?? 0) * 100))
                const aiDetectedLine = isMissed ? null : `AI detected: "${aiLabel}" with ${aiConfPct}% confidence`

                if (inputKind === 'none') {
                  if (correctionType === 'not_relevant') {
                    const aiLabelForCopy = activeCorrection.item?.refined_label || activeCorrection.item?.label || 'this item'
                    return (
                      <div style={{
                        marginBottom: 12, padding: '10px 12px',
                        background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8,
                        fontFamily: fs, fontSize: 13, color: C.brown, lineHeight: 1.5,
                      }}>
                        AI correctly detected this as: <strong>"{aiLabelForCopy}"</strong>.<br />
                        <span style={{ color: C.muted }}>Marking it as not worth tracking will train Casavio to filter similar items from inventory automatically in the future.</span>
                      </div>
                    )
                  }
                  return (
                    <div style={{
                      marginBottom: 12, padding: '10px 12px',
                      background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8,
                      fontFamily: fs, fontSize: 13, color: C.brown, lineHeight: 1.5,
                    }}>
                      {aiDetectedLine}.<br />
                      <span style={{ color: C.muted }}>Saving this correction tells Casavio this detection shouldn't exist.</span>
                    </div>
                  )
                }

                if (inputKind === 'duplicate') {
                  const candidates = items
                    .map((it, i) => ({ it, i }))
                    .filter(({ i }) => i !== activeCorrection.itemIndex)
                    .filter(({ it }) => !!it.detection_id)
                  if (candidates.length === 0) {
                    return (
                      <div style={{ marginBottom: 12 }}>
                        <div style={{ fontSize: 13, color: C.muted, fontStyle: 'italic', marginBottom: 4 }}>
                          {aiDetectedLine}.
                        </div>
                        <div style={{ fontSize: 13, color: C.danger, fontFamily: fs }}>
                          No other detections in this photo to mark as a duplicate of. Try "False positive" instead?
                        </div>
                      </div>
                    )
                  }
                  return (
                    <div style={{ marginBottom: 12 }}>
                      <label style={labelStyle}>Duplicate of which detection?</label>
                      <select
                        value={correctionValue}
                        onChange={e => setCorrectionValue(e.target.value)}
                        className="os-sandbox-select"
                        style={selectStyle}
                      >
                        <option value="">— Pick the original —</option>
                        {candidates.map(({ it, i }) => (
                          <option key={it.detection_id} value={it.detection_id}>
                            {it.refined_label || it.label || `Item ${i + 1}`}
                          </option>
                        ))}
                      </select>
                      <div style={{ fontSize: 12, color: C.muted, marginTop: 6, fontStyle: 'italic' }}>
                        {aiDetectedLine}.
                      </div>
                    </div>
                  )
                }

                const aiVal = isMissed ? null : aiValueForType(activeCorrection.item, correctionType)
                return (
                  <div style={{ marginBottom: 12 }}>
                    <label style={labelStyle}>{isMissed ? 'What item did Casavio miss?' : 'Correct value'}</label>
                    <input
                      type="text"
                      value={correctionValue}
                      onChange={e => setCorrectionValue(e.target.value)}
                      placeholder={isMissed ? 'e.g. Sub-Zero BI-36U refrigerator' : 'Type the correct value'}
                      style={inputStyle}
                      autoFocus
                    />
                    {!isMissed && (
                      <div style={{ fontSize: 12, color: C.muted, marginTop: 6, fontStyle: 'italic' }}>
                        AI said: {aiVal ? `"${aiVal}"` : '(empty)'}
                      </div>
                    )}
                  </div>
                )
              })()}

              <div style={{ marginBottom: 14 }}>
                <label style={labelStyle}>Notes (optional)</label>
                <input
                  type="text"
                  value={correctionNotes}
                  onChange={e => setCorrectionNotes(e.target.value)}
                  placeholder={correctionType === 'not_relevant' && activeCorrection.mode === 'detection'
                    ? 'e.g. consumable, packaging, daily clutter'
                    : 'Anything else worth noting'}
                  style={inputStyle}
                />
              </div>

              {(() => {
                const isMissed = activeCorrection.mode === 'missed'
                const typeDef = CORRECTION_TYPES.find(t => t.value === correctionType)
                const inputKind = isMissed ? 'text' : (typeDef?.inputKind ?? 'text')
                const hasType = isMissed || correctionType !== ''
                const canSave = hasType && (inputKind === 'none' || correctionValue.trim().length > 0)
                const disabled = savingCorrection || !canSave
                return (
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      onClick={() => {
                        // Skip batch-counter bump on edits — corrections_count
                        // tracks distinct actions; an edit refines an existing
                        // one rather than creating new training data.
                        const wasEditing = editingCorrectionId !== null
                        handleSaveCorrection()
                        if (!wasEditing) bumpBatchFromGalleryItem(galleryItem)
                      }}
                      disabled={disabled}
                      style={{ ...primaryBtn, opacity: disabled ? 0.5 : 1, cursor: disabled ? 'not-allowed' : 'pointer' }}
                    >
                      {savingCorrection
                        ? 'Saving…'
                        : (editingCorrectionId ? 'Save edit' : 'Save correction')}
                    </button>
                    <button
                      onClick={() => { setActiveCorrection(null); setEditingCorrectionId(null) }}
                      style={secondaryBtn}
                    >
                      Cancel
                    </button>
                  </div>
                )
              })()}
            </div>
          )}

          {/* Photo-level corrections section — lists ALL active
              corrections for this session, regardless of whether they're
              attached to a detection or to the photo as a whole. Fixes
              the gap where zero-detection photos (e.g. a data-plate
              close-up) had nowhere to surface their corrections, since
              the per-detection chip rendering had no detection row to
              attach to. */}
          {sessionCorrections.length > 0 && (
            <div style={cardStyle}>
              <h3 style={{ fontFamily: f, fontSize: 15, color: C.brown, margin: '0 0 10px' }}>
                Corrections on this photo ({sessionCorrections.length})
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {sessionCorrections.map(c => {
                  const detItem = c.detection_id
                    ? drillInItems.find(it => it.detection_id === c.detection_id)
                    : null
                  const detLabel = detItem?.refined_label || detItem?.label || null
                  const indicator = c.detection_id
                    ? (detLabel
                        ? `on detection: "${detLabel}"`
                        : 'on detection: (deleted)')
                    : null
                  return (
                    <div key={c.id} style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                      <span
                        onClick={() => startEditCorrection(c)}
                        title={c.notes ? `Notes: ${c.notes}` : 'Click to edit'}
                        style={{
                          display: 'inline-flex', alignItems: 'center', gap: 4,
                          background: C.bg, border: `1px solid ${C.border}`,
                          color: C.brown, borderRadius: 12,
                          padding: '4px 4px 4px 10px',
                          fontFamily: fs, fontSize: 12, fontWeight: 600,
                          cursor: 'pointer',
                          alignSelf: 'flex-start',
                          maxWidth: '100%',
                        }}
                      >
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 360 }}>
                          {chipLabelExpanded(c)}
                        </span>
                        <button
                          onClick={e => {
                            e.stopPropagation()
                            setConfirmDeleteCorrection({ correctionId: c.id, label: chipLabelExpanded(c) })
                          }}
                          aria-label="Remove correction"
                          className="os-sandbox-chip-x"
                          style={{
                            background: 'transparent', border: 'none', padding: 0,
                            width: 18, height: 18, borderRadius: '50%',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            cursor: 'pointer', color: C.muted, lineHeight: 0,
                          }}
                        >
                          <X size={12} strokeWidth={2.4} />
                        </button>
                      </span>
                      {indicator && (
                        <span style={{
                          fontFamily: fs, fontSize: 11, color: C.muted,
                          marginLeft: 4, fontStyle: 'italic',
                        }}>
                          {indicator}
                        </span>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          {/* Detection list — grouped by Claude's should_log judgment.
              Casavio items (should_log=true) = what a production user
              would see. Filtered out (should_log=false) = diagnostic-only
              for sandbox owner to verify the filter judgment. */}
          {(() => {
            const casavioGroup = items
              .map((it, i) => ({ it, i }))
              .filter(({ it }) => it?.ai_details?.should_log !== false)
            const filteredGroup = items
              .map((it, i) => ({ it, i }))
              .filter(({ it }) => it?.ai_details?.should_log === false)

            const renderDetectionRow = ({ it, i }) => {
              const d = it.ai_details || {}
              const isFilteredRow = d.should_log === false
              const detCorrections = it.detection_id
                ? sessionCorrections.filter(c => c.detection_id === it.detection_id)
                : []
              return (
                <div key={i} style={{ marginBottom: 6 }}>
                  <div
                    onClick={() => startCorrectingDetection(i)}
                    style={{
                      padding: '10px 12px',
                      background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8,
                      cursor: 'pointer',
                      opacity: isFilteredRow ? 0.75 : 1,
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
                      <div style={{ fontWeight: 600, color: C.brown, fontFamily: fs }}>
                        {it.refined_label || it.label || `Item ${i + 1}`}
                      </div>
                      <div style={{ fontSize: 11, color: C.muted, whiteSpace: 'nowrap' }}>
                        {it.confidence_band} · {((it.confidence_score ?? 0) * 100).toFixed(0)}%
                      </div>
                    </div>
                    <div style={{ fontSize: 12, color: C.muted, marginTop: 4, display: 'flex', flexWrap: 'wrap', gap: '4px 14px' }}>
                      {d.brand?.value && <span>Brand: {d.brand.value}</span>}
                      {d.model?.value && <span>Model: {d.model.value}</span>}
                      {d.category_suggestion && <span>Category: {d.category_suggestion}</span>}
                      {d.color && <span>Color: {d.color}</span>}
                    </div>
                  </div>
                  {!isFilteredRow && it.detection_id && (
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: 8,
                      flexWrap: 'wrap', padding: '6px 6px 0', marginLeft: 6,
                    }}>
                      <button
                        onClick={e => { e.stopPropagation(); handleDeepRead(it.detection_id) }}
                        disabled={deepReadingDetectionId === it.detection_id}
                        className="os-sandbox-readlabel"
                        style={{
                          background: C.bg, border: `1px solid ${C.border}`,
                          color: C.amber, borderRadius: 12,
                          padding: '4px 10px',
                          fontFamily: fs, fontSize: 12, fontWeight: 600,
                          cursor: deepReadingDetectionId === it.detection_id ? 'wait' : 'pointer',
                          display: 'inline-flex', alignItems: 'center', gap: 6,
                          opacity: deepReadingDetectionId === it.detection_id ? 0.7 : 1,
                        }}
                      >
                        {deepReadingDetectionId === it.detection_id ? (
                          <>
                            <Loader size={11} color={C.amber} strokeWidth={2.5} style={{ animation: 'ownersandbox-spin 1s linear infinite' }} />
                            Reading…
                          </>
                        ) : (
                          it.last_deep_read_at ? 'Re-read this label' : 'Read this label'
                        )}
                      </button>
                      {it.last_deep_read_at && deepReadingDetectionId !== it.detection_id && (
                        <span style={{ fontSize: 11, color: C.muted, fontFamily: fs }}>
                          deep-read {relTime(it.last_deep_read_at)}
                        </span>
                      )}
                    </div>
                  )}
                  {detCorrections.length > 0 && (
                    <div style={{
                      display: 'flex', flexWrap: 'wrap', gap: 6,
                      padding: '6px 6px 0', marginLeft: 6,
                    }}>
                      {detCorrections.map(c => (
                        <span
                          key={c.id}
                          onClick={e => { e.stopPropagation(); startEditCorrection(c) }}
                          title={c.notes ? `Notes: ${c.notes}` : 'Click to edit'}
                          style={{
                            display: 'inline-flex', alignItems: 'center', gap: 4,
                            background: C.bg, border: `1px solid ${C.border}`,
                            color: C.brown, borderRadius: 12,
                            padding: '3px 4px 3px 8px',
                            fontFamily: fs, fontSize: 12, fontWeight: 600,
                            cursor: 'pointer',
                            maxWidth: '100%',
                          }}
                        >
                          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 240 }}>
                            {chipLabelFor(c)}
                          </span>
                          <button
                            onClick={e => {
                              e.stopPropagation()
                              setConfirmDeleteCorrection({ correctionId: c.id, label: chipLabelFor(c) })
                            }}
                            aria-label="Remove correction"
                            className="os-sandbox-chip-x"
                            style={{
                              background: 'transparent', border: 'none', padding: 0,
                              width: 16, height: 16, borderRadius: '50%',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              cursor: 'pointer', color: C.muted, lineHeight: 0,
                            }}
                          >
                            <X size={11} strokeWidth={2.4} />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              )
            }

            return (
              <>
                {/* Primary section: what production users would see */}
                <div style={cardStyle}>
                  <h3 style={{ fontFamily: f, fontSize: 15, color: C.brown, margin: '0 0 12px' }}>
                    Casavio items ({casavioGroup.length})
                  </h3>
                  {drillInLoading && (
                    <div style={{ textAlign: 'center', padding: 20 }}>
                      <Loader size={20} color={C.amber} strokeWidth={1.5} style={{ animation: 'ownersandbox-spin 1s linear infinite' }} />
                    </div>
                  )}
                  {!drillInLoading && casavioGroup.length === 0 && items.length === 0 && (
                    <div style={{ fontSize: 13, color: C.muted, fontStyle: 'italic' }}>
                      No detections. If this is correct (empty / uninteresting frame), no correction is needed — the absence is itself a confidence signal.
                    </div>
                  )}
                  {!drillInLoading && casavioGroup.length === 0 && filteredGroup.length > 0 && (
                    <div style={{ fontSize: 13, color: C.muted, fontStyle: 'italic' }}>
                      Nothing in this photo was judged worth tracking. All detections landed in "Filtered out" below — open it and use a correction if Casavio's filter judgment was wrong.
                    </div>
                  )}
                  {casavioGroup.map(renderDetectionRow)}
                </div>

                {/* Diagnostic section: filter judgment verification */}
                {filteredGroup.length > 0 && (
                  <div style={cardStyle}>
                    <div
                      onClick={() => setFilteredOutExpanded(v => !v)}
                      style={{
                        cursor: 'pointer', userSelect: 'none',
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        marginBottom: filteredOutExpanded ? 12 : 0,
                      }}
                    >
                      <h3 style={{ fontFamily: f, fontSize: 15, color: C.muted, margin: 0 }}>
                        Filtered out ({filteredGroup.length})
                        <span style={{ fontSize: 11, fontFamily: fs, color: C.muted, fontWeight: 400, marginLeft: 6 }}>
                          {filteredOutExpanded ? '— tap to collapse' : '— would be hidden from users'}
                        </span>
                      </h3>
                      <span style={{ fontSize: 14, color: C.muted, lineHeight: 1, marginLeft: 8 }}>{filteredOutExpanded ? '▾' : '▸'}</span>
                    </div>
                    {!filteredOutExpanded && (
                      <div style={{ fontSize: 12, color: C.muted, fontStyle: 'italic', marginTop: 6 }}>
                        {filteredGroup
                          .map(({ it }) => it.ai_details?.category_suggestion || it.refined_label || it.label || 'item')
                          .slice(0, 6)
                          .join(' · ')}
                        {filteredGroup.length > 6 && ' · …'}
                      </div>
                    )}
                    {filteredOutExpanded && filteredGroup.map(renderDetectionRow)}
                  </div>
                )}
              </>
            )
          })()}
        </div>
      </div>
    )
  }

  // Fire-and-forget batch corrections-count bump. Pulls batch_id from
  // room_scan_sessions for the current drill-in session, then increments.
  // Race-tolerant for single-user owner.
  async function bumpBatchFromGalleryItem(galleryItem) {
    if (!galleryItem) return
    try {
      const { data } = await supabase
        .from('room_scan_sessions')
        .select('batch_id')
        .eq('id', galleryItem.sessionId)
        .maybeSingle()
      if (data?.batch_id) bumpBatchCorrectionsCount(data.batch_id)
    } catch (err) {
      console.error('[OwnerSandbox] bumpBatchFromGalleryItem failed', err)
    }
  }

  // ----- Top-level render ---------------------------------------------

  return (
    <div style={{ minHeight: '100vh', background: C.bg }}>
      <style>{SPIN}</style>

      {pasteToast && (
        <div style={{
          position: 'fixed',
          top: 'calc(20px + env(safe-area-inset-top))',
          left: '50%', transform: 'translateX(-50%)',
          zIndex: 2100,
          background: C.successBg, color: C.success,
          padding: '8px 16px', borderRadius: 999,
          fontFamily: fs, fontSize: 13, fontWeight: 700,
          letterSpacing: 0.2,
          boxShadow: '0 2px 8px rgba(0,0,0,0.18)',
          pointerEvents: 'none',
        }}>
          {pasteToast}
        </div>
      )}

      <div style={wrap}>
        <button onClick={onBack} style={{ ...linkBtn, marginBottom: 10 }}>
          <ArrowLeft size={14} /> Owner Dashboard
        </button>
        <h1 style={{ fontFamily: f, fontSize: 22, color: C.brown, margin: '0 0 10px' }}>
          AI Calibration Sandbox
        </h1>

        {viewingGroupId ? (
          <>
            <div style={{ marginBottom: 14 }}>
              <button onClick={closeGroupDetail} style={linkBtn}>
                <ArrowLeft size={14} /> Back to gallery
              </button>
            </div>
            {renderGroupedObject()}
          </>
        ) : (
          <>
            {/* Gallery / Analytics tabs. Underline-style active state matches
                the chevron/select treatment used elsewhere in the sandbox. */}
            <div style={{
              display: 'flex', gap: 24,
              borderBottom: `1px solid ${C.border}`,
              marginBottom: 14,
            }}>
              {[
                { key: 'gallery', label: 'Gallery' },
                { key: 'analytics', label: 'Analytics' },
              ].map(t => {
                const isActive = sandboxTab === t.key
                return (
                  <button
                    key={t.key}
                    onClick={() => setSandboxTab(t.key)}
                    style={{
                      background: 'transparent', border: 'none', padding: '8px 2px',
                      fontFamily: f, fontSize: 14, fontWeight: 700,
                      color: isActive ? C.amber : C.muted,
                      borderBottom: `2px solid ${isActive ? C.amber : 'transparent'}`,
                      marginBottom: -1,
                      cursor: 'pointer',
                      letterSpacing: 0.2,
                    }}
                  >
                    {t.label}
                  </button>
                )
              })}
            </div>

            {sandboxTab === 'gallery' ? (
              <>
                {renderUploadCard()}
                {renderInFlightPill()}
                {renderGallery()}
                {recentCorrections.length > 0 && (
                  <div style={{ marginTop: 16 }}>
                    {renderRecentCorrections()}
                  </div>
                )}
              </>
            ) : (
              renderAnalytics()
            )}
          </>
        )}
      </div>

      {drillInSessionId !== null && renderDrillIn()}

      {/* Floating action bar — appears when at least one photo is selected.
          Group button enables at 2+ selections; below that it's disabled
          to prevent a one-photo "group". */}
      {selectMode && selectedSessionIds.size > 0 && (
        <div style={{
          position: 'fixed',
          left: 0, right: 0,
          bottom: 'calc(env(safe-area-inset-bottom) + 12px)',
          zIndex: 2050,
          display: 'flex', justifyContent: 'center',
          pointerEvents: 'none',
        }}>
          <div style={{
            background: C.brown, color: '#fff',
            borderRadius: 999,
            padding: '8px 8px 8px 18px',
            display: 'inline-flex', alignItems: 'center', gap: 12,
            boxShadow: '0 4px 12px rgba(0,0,0,0.35)',
            fontFamily: fs, fontSize: 13, fontWeight: 600,
            pointerEvents: 'auto',
            maxWidth: 'calc(100vw - 24px)',
          }}>
            <span>{selectedSessionIds.size} selected</span>
            <button
              onClick={openGroupModal}
              disabled={selectedSessionIds.size < 2}
              style={{
                background: selectedSessionIds.size < 2 ? 'rgba(255,255,255,0.18)' : C.amber,
                color: '#fff',
                border: 'none', borderRadius: 999,
                padding: '8px 14px',
                fontFamily: fs, fontSize: 13, fontWeight: 700,
                cursor: selectedSessionIds.size < 2 ? 'not-allowed' : 'pointer',
                opacity: selectedSessionIds.size < 2 ? 0.6 : 1,
                display: 'inline-flex', alignItems: 'center', gap: 6,
              }}
            >
              <Layers size={13} strokeWidth={2.4} />
              Group as one object
            </button>
            <button
              onClick={cancelSelectMode}
              style={{
                background: 'transparent', color: '#fff',
                border: '1px solid rgba(255,255,255,0.3)', borderRadius: 999,
                padding: '7px 12px',
                fontFamily: fs, fontSize: 13, fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {confirmDeleteCorrection && (
        <div
          onClick={() => setConfirmDeleteCorrection(null)}
          style={{
            position: 'fixed', inset: 0, zIndex: 2300,
            background: 'rgba(44,29,20,0.78)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 20,
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: C.card, borderRadius: 12,
              padding: 22, maxWidth: 380, width: '100%',
              fontFamily: fs, color: C.brown,
              boxShadow: '0 6px 24px rgba(0,0,0,0.35)',
            }}
          >
            <h3 style={{ fontFamily: f, fontSize: 16, color: C.brown, margin: '0 0 8px' }}>
              Remove this correction?
            </h3>
            {confirmDeleteCorrection.label && (
              <div style={{
                background: C.bg, border: `1px solid ${C.border}`,
                borderRadius: 8, padding: '6px 10px',
                fontSize: 12, color: C.muted, margin: '0 0 12px',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>
                {confirmDeleteCorrection.label}
              </div>
            )}
            <p style={{ fontSize: 13, color: C.brown, lineHeight: 1.5, margin: '0 0 16px' }}>
              It will no longer train the filter, but stays in history.
            </p>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={() => setConfirmDeleteCorrection(null)} style={secondaryBtn}>
                Cancel
              </button>
              <button
                onClick={() => performSoftDeleteCorrection(confirmDeleteCorrection.correctionId)}
                style={{
                  background: C.danger, color: '#fff', border: 'none', borderRadius: 10,
                  padding: '10px 18px', fontFamily: f, fontSize: 14, fontWeight: 700,
                  cursor: 'pointer',
                  boxShadow: '0 2px 6px rgba(176,48,32,0.25)',
                }}
              >
                Remove
              </button>
            </div>
          </div>
        </div>
      )}

      {groupModalOpen && (() => {
        const selectedItems = galleryItems.filter(g => selectedSessionIds.has(g.sessionId))
        const canSave = !creatingGroup && groupForm.label.trim().length > 0
        return (
          <div
            onClick={() => !creatingGroup && closeGroupModal()}
            style={{
              position: 'fixed', inset: 0, zIndex: 2400,
              background: 'rgba(44,29,20,0.78)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              padding: 20, overflowY: 'auto',
            }}
          >
            <div
              onClick={e => e.stopPropagation()}
              style={{
                background: C.card, borderRadius: 12,
                padding: 22, maxWidth: 520, width: '100%',
                fontFamily: fs, color: C.brown,
                boxShadow: '0 6px 24px rgba(0,0,0,0.35)',
                maxHeight: '90vh', overflowY: 'auto',
              }}
            >
              <h3 style={{ fontFamily: f, fontSize: 17, color: C.brown, margin: '0 0 8px', display: 'flex', alignItems: 'center', gap: 10 }}>
                Group {selectedItems.length} photos as one object?
                {prefillLoading && (
                  <span style={{ fontFamily: fs, fontSize: 11, fontWeight: 400, color: C.muted, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    <Loader size={11} color={C.muted} strokeWidth={2.5} style={{ animation: 'ownersandbox-spin 1s linear infinite' }} />
                    Loading suggestions…
                  </span>
                )}
              </h3>
              <p style={{ fontSize: 12, color: C.muted, margin: '0 0 14px', lineHeight: 1.4 }}>
                These photos will be linked to one object record. You can ungroup later.
              </p>
              <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 6, marginBottom: 14 }}>
                {selectedItems.map(g => (
                  <img
                    key={g.sessionId}
                    src={g.photoUrl}
                    alt=""
                    style={{
                      width: 56, height: 56, objectFit: 'cover', borderRadius: 6,
                      flexShrink: 0, background: '#000',
                    }}
                  />
                ))}
              </div>

              {/* Per-field render: select when AI returned distinct values
                  AND user hasn't switched to "Type new". Otherwise input. */}
              {(() => {
                const renderPrefillField = ({ field, placeholderText, label }) => {
                  const meta = prefillModes[field] || { mode: 'empty', options: [] }
                  const isCustom = customFieldModes.has(field)
                  const showSelect = meta.mode === 'multi' && !isCustom
                  if (showSelect) {
                    return (
                      <select
                        value={groupForm[field]}
                        onChange={e => {
                          const v = e.target.value
                          if (v === '__custom__') toggleCustomField(field)
                          else setGroupForm(f => ({ ...f, [field]: v }))
                        }}
                        className="os-sandbox-select"
                        style={selectStyle}
                        disabled={creatingGroup}
                      >
                        <option value="" disabled>— Choose {label.toLowerCase()} —</option>
                        {meta.options.map(opt => (
                          <option key={opt.value} value={opt.value}>
                            {opt.value} ({opt.count} photo{opt.count === 1 ? '' : 's'})
                          </option>
                        ))}
                        <option value="__custom__">Type a new {label.toLowerCase()}…</option>
                      </select>
                    )
                  }
                  return (
                    <input
                      type="text"
                      value={groupForm[field]}
                      onChange={e => setGroupForm(f => ({ ...f, [field]: e.target.value }))}
                      placeholder={placeholderText}
                      style={inputStyle}
                      disabled={creatingGroup}
                      autoFocus={field === 'label' && meta.mode !== 'single'}
                    />
                  )
                }
                return (
                  <>
                    <div style={{ marginBottom: 10 }}>
                      <label style={labelStyle}>Object label *</label>
                      {renderPrefillField({ field: 'label', placeholderText: 'e.g. KitchenAid stand mixer', label: 'label' })}
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
                      <div>
                        <label style={labelStyle}>Make</label>
                        {renderPrefillField({ field: 'make', placeholderText: 'e.g. KitchenAid', label: 'make' })}
                      </div>
                      <div>
                        <label style={labelStyle}>Model</label>
                        {renderPrefillField({ field: 'model', placeholderText: 'e.g. Pro 600', label: 'model' })}
                      </div>
                    </div>
                    <div style={{ marginBottom: 10 }}>
                      <label style={labelStyle}>Serial</label>
                      {renderPrefillField({ field: 'serial', placeholderText: 'Serial number', label: 'serial' })}
                    </div>
                  </>
                )
              })()}

              {/* Category — always a select; placeholder + options shape
                  varies by prefill mode. Distinct extracted values are
                  surfaced first, then the full category list as fallback
                  (deduped). */}
              {(() => {
                const meta = prefillModes.category || { mode: 'empty', options: [] }
                const extracted = meta.options.map(o => o.value)
                const fallback = SANDBOX_OBJECT_CATEGORIES.filter(c => !extracted.includes(c))
                const placeholder = meta.mode === 'multi'
                  ? '— Multiple values, choose one —'
                  : 'Choose category…'
                return (
                  <div style={{ marginBottom: 10 }}>
                    <label style={labelStyle}>Category</label>
                    <select
                      value={groupForm.category}
                      onChange={e => setGroupForm(f => ({ ...f, category: e.target.value }))}
                      className="os-sandbox-select"
                      style={selectStyle}
                      disabled={creatingGroup}
                    >
                      <option value="" disabled>{placeholder}</option>
                      {extracted.map(c => {
                        const count = meta.options.find(o => o.value === c)?.count ?? 0
                        return (
                          <option key={`x-${c}`} value={c}>
                            {c}{meta.mode === 'multi' ? ` (${count} photo${count === 1 ? '' : 's'})` : ''}
                          </option>
                        )
                      })}
                      {fallback.map(c => (
                        <option key={`f-${c}`} value={c}>{c}</option>
                      ))}
                    </select>
                  </div>
                )
              })()}
              <div style={{ marginBottom: 16 }}>
                <label style={labelStyle}>Notes</label>
                <input
                  type="text"
                  value={groupForm.notes}
                  onChange={e => setGroupForm(f => ({ ...f, notes: e.target.value }))}
                  placeholder="Anything else worth noting"
                  style={inputStyle}
                  disabled={creatingGroup}
                />
              </div>

              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button
                  onClick={closeGroupModal}
                  disabled={creatingGroup}
                  style={{ ...secondaryBtn, opacity: creatingGroup ? 0.5 : 1, cursor: creatingGroup ? 'not-allowed' : 'pointer' }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleGroupSubmit}
                  disabled={!canSave}
                  style={{
                    ...primaryBtn,
                    opacity: canSave ? 1 : 0.5,
                    cursor: canSave ? 'pointer' : 'not-allowed',
                  }}
                >
                  {creatingGroup ? 'Grouping…' : 'Group'}
                </button>
              </div>
            </div>
          </div>
        )
      })()}

      {confirmUngroup && (
        <div
          onClick={() => !ungrouping && setConfirmUngroup(false)}
          style={{
            position: 'fixed', inset: 0, zIndex: 2450,
            background: 'rgba(44,29,20,0.78)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 20,
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: C.card, borderRadius: 12,
              padding: 22, maxWidth: 380, width: '100%',
              fontFamily: fs, color: C.brown,
              boxShadow: '0 6px 24px rgba(0,0,0,0.35)',
            }}
          >
            <h3 style={{ fontFamily: f, fontSize: 16, color: C.brown, margin: '0 0 10px' }}>
              Ungroup these {viewingGroupData?.sessions?.length ?? 0} photos?
            </h3>
            <p style={{ fontSize: 13, color: C.brown, lineHeight: 1.5, margin: '0 0 16px' }}>
              The photos and their corrections stay intact. Only the grouping is removed.
            </p>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={() => setConfirmUngroup(false)} disabled={ungrouping} style={{ ...secondaryBtn, opacity: ungrouping ? 0.5 : 1, cursor: ungrouping ? 'not-allowed' : 'pointer' }}>
                Cancel
              </button>
              <button
                onClick={handleUngroup}
                disabled={ungrouping}
                style={{
                  background: C.danger, color: '#fff', border: 'none', borderRadius: 10,
                  padding: '10px 18px', fontFamily: f, fontSize: 14, fontWeight: 700,
                  cursor: ungrouping ? 'not-allowed' : 'pointer',
                  opacity: ungrouping ? 0.6 : 1,
                  boxShadow: '0 2px 6px rgba(176,48,32,0.25)',
                }}
              >
                {ungrouping ? 'Ungrouping…' : 'Ungroup'}
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmDeleteSession && (
        <div
          onClick={() => !deleting && setConfirmDeleteSession(null)}
          style={{
            position: 'fixed', inset: 0, zIndex: 2200,
            background: 'rgba(44,29,20,0.78)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 20,
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: C.card, borderRadius: 12,
              padding: 22, maxWidth: 420, width: '100%',
              fontFamily: fs, color: C.brown,
              boxShadow: '0 6px 24px rgba(0,0,0,0.35)',
            }}
          >
            <h3 style={{ fontFamily: f, fontSize: 17, color: C.brown, margin: '0 0 10px' }}>
              Delete this photo?
            </h3>
            <p style={{ fontSize: 13, color: C.brown, lineHeight: 1.5, margin: '0 0 18px' }}>
              This removes the photo, all detections, and all corrections for it.
              This cannot be undone.
            </p>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button
                onClick={() => setConfirmDeleteSession(null)}
                disabled={deleting}
                style={{ ...secondaryBtn, opacity: deleting ? 0.5 : 1, cursor: deleting ? 'not-allowed' : 'pointer' }}
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteSession(confirmDeleteSession)}
                disabled={deleting}
                style={{
                  background: C.danger, color: '#fff', border: 'none', borderRadius: 10,
                  padding: '10px 18px', fontFamily: f, fontSize: 14, fontWeight: 700,
                  cursor: deleting ? 'not-allowed' : 'pointer',
                  opacity: deleting ? 0.6 : 1,
                  boxShadow: '0 2px 6px rgba(176,48,32,0.25)',
                }}
              >
                {deleting ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
